import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Target, Database, FolderKanban, Headphones, Shield, Lightbulb, Sparkles,
  TrendingUp, CheckCircle, Clock, Users, DollarSign, FileText,
  BarChart3, Calendar, Award, Zap, Building2, Mail, BookOpen, Crown, Briefcase, Link2, FileSpreadsheet, Download
} from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  piliers, 
  tactiques, 
  clientMockup, 
  kpiGlobaux, 
  roadmap 
} from '../data/cadreReferentielBZ';
import { livrablesTangiblesParTactique } from '../data/livrablesTangiblesParTactique';
import {
  PiliersTab as EbaucheCadreBZPiliersTab,
  TransversalesTab as EbaucheCadreBZTransversalesTab,
} from './EbaucheCadreBZModal';

interface CadreReferentielModalProps {
  isOpen: boolean;
  onClose: () => void;
  hiddenTabIds?: string[];
  includeEbaucheCadreBZTabs?: boolean;
  hideHeaderStats?: boolean;
}

const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
  Target, Database, FolderKanban, Headphones, Shield, Lightbulb, Sparkles
};

const CadreReferentielModal: React.FC<CadreReferentielModalProps> = ({
  isOpen,
  onClose,
  hiddenTabIds = [],
  includeEbaucheCadreBZTabs = false,
  hideHeaderStats = false,
}) => {
  const [activeTab, setActiveTab] = useState('methodologie');
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const hiddenTabs = useMemo(() => new Set(hiddenTabIds), [hiddenTabIds]);
  
  // Onglets (doit être défini avant tout return conditionnel)
  const tabs = useMemo(
    () => {
      const baseTabs = [
      { id: 'methodologie', label: 'Les 3 Niveaux', icon: Target },
      { id: 'raison', label: "Raison d'être", icon: BookOpen },
      { id: 'overview', label: "Vue d'ensemble", icon: Target },
      { id: 'solutions', label: "Solutions d'Affaires", icon: Briefcase },
      { id: 'piliers', label: 'Piliers détaillés', icon: Database },
      { id: 'tactiques', label: 'Tactiques', icon: CheckCircle },
      { id: 'livrables', label: 'Livrables Tangibles', icon: FileSpreadsheet },
      { id: 'spider', label: 'Spider Charts', icon: BarChart3 },
      { id: 'kpi', label: 'KPI & Métriques', icon: TrendingUp },
      { id: 'roadmap', label: 'Roadmap', icon: Calendar },
      { id: 'gouvernance', label: 'Gouvernance', icon: Award },
      { id: 'client', label: 'Profil Client', icon: Building2 },
      ].filter((tab) => !hiddenTabs.has(tab.id));

      if (includeEbaucheCadreBZTabs) {
        baseTabs.push(
          { id: 'ebauche-piliers', label: '6 piliers', icon: Target },
          { id: 'ebauche-transversales', label: 'Transversales', icon: Users },
        );
      }

      return baseTabs;
    },
    [hiddenTabs, includeEbaucheCadreBZTabs]
  );

  const tabLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tabs) map.set(t.id, t.label);
    return map;
  }, [tabs]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [activeTab, tabs]);

  // IMPORTANT: après les hooks uniquement
  if (!isOpen) return null;

  // Préparer données spider chart
  const spiderData = piliers.map(p => ({
    pilier: p.nom.split(' ')[0], // Premier mot
    'As-Was': p.asWas,
    'As-Is': p.asIs,
    'Target': p.target
  }));

  // Calculer statistiques globales
  const statsGlobales = {
    maturiteGlobale: (piliers.reduce((sum, p) => sum + p.asIs, 0) / piliers.length).toFixed(1),
    progression: ((piliers.reduce((sum, p) => sum + p.asIs, 0) / piliers.length) - (piliers.reduce((sum, p) => sum + p.asWas, 0) / piliers.length)).toFixed(1),
    tactiquesActives: piliers.reduce((sum, p) => sum + p.tactiquesActives, 0),
    tactiquesTotal: 65,
    couverture: Math.round((piliers.reduce((sum, p) => sum + p.tactiquesActives, 0) / 69) * 100)
  };

  const getCurrentTabText = (): string => {
    const base = (contentRef.current?.innerText || '').trim();

    // Ajouter des valeurs structurées utiles quand les onglets sont très "visuels"
    if (activeTab === 'spider') {
      const lines = piliers.map(
        (p) => `- ${p.nom}: As-Was ${p.asWas}/5 • As-Is ${p.asIs}/5 • Target ${p.target}/5`
      );
      return `${base}\n\nDonnées (valeurs):\n${lines.join('\n')}`.trim();
    }

    if (activeTab === 'kpi') {
      const lines = piliers.map((p) => {
        const kpi = (kpiGlobaux as any)[`pilier${p.id}`];
        if (!kpi) return `- ${p.nom}: —`;
        return `- ${p.nom}: ${kpi.principal} | ${kpi.secondaire} | ${kpi.tendance}`;
      });
      return `${base}\n\nKPIs (liste):\n${lines.join('\n')}`.trim();
    }

    if (activeTab === 'roadmap') {
      const lines = (roadmap || []).map((r: any) => {
        const priorites = Array.isArray(r.priorites) ? r.priorites.join(' | ') : '';
        return `- ${r.trimestre || 'Trimestre'}: ${priorites}${r.budget ? ` (Budget: ${r.budget})` : ''}`;
      });
      return `${base}\n\nRoadmap (liste):\n${lines.join('\n')}`.trim();
    }

    return base;
  };

  const exportCurrentTabPdf = async () => {
    setIsExportingPdf(true);
    try {
      const { exportTextPdf } = await import('../services/textPdfExporter');
      const label = tabLabelById.get(activeTab) || activeTab;
      const text = getCurrentTabText();
      await exportTextPdf({
        filename: `cadre-bz-${activeTab}.pdf`,
        title: `Cadre Référentiel BZ — ${label}`,
        sections: [{ title: label, text }],
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const exportAllTabsPdf = async () => {
    if (isExportingPdf) return;
    setIsExportingPdf(true);
    const previous = activeTab;
    try {
      const { exportTextPdf } = await import('../services/textPdfExporter');
      const sections: { title: string; text: string }[] = [];

      for (const t of tabs) {
        setActiveTab(t.id);
        // Laisser le temps au contenu + animation de se rendre
        await new Promise((r) => setTimeout(r, 250));
        const text = (contentRef.current?.innerText || '').trim();

        // Ajouter enrichissements pour certains onglets
        let finalText = text;
        if (t.id === 'spider') {
          const lines = piliers.map(
            (p) => `- ${p.nom}: As-Was ${p.asWas}/5 • As-Is ${p.asIs}/5 • Target ${p.target}/5`
          );
          finalText = `${finalText}\n\nDonnées (valeurs):\n${lines.join('\n')}`.trim();
        }
        if (t.id === 'kpi') {
          const lines = piliers.map((p) => {
            const kpi = (kpiGlobaux as any)[`pilier${p.id}`];
            if (!kpi) return `- ${p.nom}: —`;
            return `- ${p.nom}: ${kpi.principal} | ${kpi.secondaire} | ${kpi.tendance}`;
          });
          finalText = `${finalText}\n\nKPIs (liste):\n${lines.join('\n')}`.trim();
        }
        if (t.id === 'roadmap') {
          const lines = (roadmap || []).map((r: any) => {
            const priorites = Array.isArray(r.priorites) ? r.priorites.join(' | ') : '';
            return `- ${r.trimestre || 'Trimestre'}: ${priorites}${r.budget ? ` (Budget: ${r.budget})` : ''}`;
          });
          finalText = `${finalText}\n\nRoadmap (liste):\n${lines.join('\n')}`.trim();
        }

        sections.push({ title: t.label, text: finalText });
      }

      await exportTextPdf({
        filename: `cadre-bz-tous-onglets.pdf`,
        title: `Cadre Référentiel BZ — Export complet`,
        sections,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setActiveTab(previous);
      setIsExportingPdf(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-7xl h-full max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col min-h-0"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 shrink-0">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <div className="relative flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-xl p-3 rounded-xl">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white mb-1">Cadre Référentiel BZ</h1>
                <p className="text-blue-100 text-lg font-semibold">Méthodologie holistique de Direction TI • 7 Piliers • 65 Tactiques</p>
              </div>
            </div>

            {/* Export PDF */}
            <div className="relative mt-4 flex items-center gap-3">
              <button
                onClick={() => void exportCurrentTabPdf()}
                disabled={isExportingPdf}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-colors ${
                  isExportingPdf ? 'bg-white/30 text-white/70 cursor-not-allowed' : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                <Download className="w-4 h-4" />
                PDF (onglet)
              </button>
              <button
                onClick={() => void exportAllTabsPdf()}
                disabled={isExportingPdf}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-colors ${
                  isExportingPdf ? 'bg-white/30 text-white/70 cursor-not-allowed' : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                <Download className="w-4 h-4" />
                PDF (tous les onglets)
              </button>
              {isExportingPdf && <span className="text-white/80 font-semibold text-sm">Génération…</span>}
            </div>

            {/* Stats rapides dans header */}
            {!hideHeaderStats && (
              <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  { label: 'Maturité Globale', value: `${statsGlobales.maturiteGlobale}/5`, icon: Award, color: 'from-green-400 to-emerald-400' },
                  { label: 'Progression', value: `+${statsGlobales.progression}`, icon: TrendingUp, color: 'from-blue-400 to-cyan-400' },
                  { label: 'Tactiques Actives', value: `${statsGlobales.tactiquesActives}/${statsGlobales.tactiquesTotal}`, icon: CheckCircle, color: 'from-purple-400 to-pink-400' },
                  { label: 'Couverture', value: `${statsGlobales.couverture}%`, icon: Zap, color: 'from-orange-400 to-red-400' },
                  { label: 'Client', value: `${clientMockup.tailleEntreprise} emp.`, icon: Users, color: 'from-indigo-400 to-violet-400' }
                ].map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/15 backdrop-blur-xl p-4 rounded-xl border border-white/20"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`bg-gradient-to-br ${stat.color} p-2 rounded-lg`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-white/80 text-sm font-medium">{stat.label}</span>
                      </div>
                      <p className="text-lg font-black text-white">{stat.value}</p>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tabs Navigation */}
          <div className="border-b border-gray-200 bg-gray-50 px-3 shrink-0">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-all whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 bg-gradient-to-br from-gray-50 to-blue-50">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                ref={contentRef}
              >
                {/* LES 3 NIVEAUX */}
                {activeTab === 'methodologie' && (
                  <div className="space-y-2">
                    {/* Titre principal */}
                    <div className="text-center mb-4">
                      <div className="inline-block bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-3 rounded-xl mb-2">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-xl font-black text-gray-900 mb-1">
                        Les 3 Niveaux de Profondeur
                      </h2>
                      <p className="text-sm text-gray-600 max-w-4xl mx-auto">
                        <strong className="text-indigo-600">PRIsME</strong> et <strong className="text-purple-600">Solutions d'Affaires</strong> couvrent les niveaux <strong className="text-yellow-600">1 (Stratégique)</strong> et <strong className="text-purple-600">2 (Tactique)</strong> de manière <strong className="text-green-600">PRO</strong>
                      </p>
                    </div>

                    {/* Pyramide visuelle des 3 niveaux */}
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 shadow-xl border border-gray-200 mb-4">
                      <div className="flex flex-col items-center space-y-2">
                        {/* Niveau 1 - STRATÉGIQUE */}
                        <div className="w-full max-w-4xl">
                          <div className="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-t-xl p-4 shadow-lg border-2 border-yellow-400 relative">
                            <div className="absolute -top-2 -right-2 bg-indigo-600 px-3 py-1 rounded-full border-2 border-white shadow-lg flex items-center gap-1">
                              <span className="text-xs font-black text-white">⭐ PRIsME</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="bg-white/30 backdrop-blur-sm p-2 rounded-lg">
                                  <Crown className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-black text-white">1. STRATÉGIQUE</h3>
                                  <p className="text-xs text-yellow-100">Vision long terme • Direction • Décisions majeures</p>
                                </div>
                              </div>
                              <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                                <span className="text-xs font-bold text-white">NIVEAU 1</span>
                              </div>
                            </div>
                            <div className="mt-3 bg-white/20 backdrop-blur-sm rounded-lg p-3">
                              <p className="text-xs font-semibold text-white mb-2">📋 Livrables PRO (ITIL/COBIT/NIST) :</p>
                              <div className="grid grid-cols-2 gap-2">
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan Directeur TI (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Stratégie TI alignée affaires (COBIT)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Vision TI 3-5 ans (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Cadre d'objectifs stratégiques TI (COBIT)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Matrice RACI gouvernance TI/Client (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan budgétaire TI pluriannuel (COBIT)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Roadmap stratégique TI (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Registre risques TI stratégiques (COBIT)</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Niveau 2 - TACTIQUE (CADRE RÉFÉRENTIEL BZ) */}
                        <div className="w-full max-w-4xl">
                          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl p-5 shadow-xl border-4 border-purple-400 relative">
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 px-4 py-1 rounded-full border-2 border-white shadow-lg flex items-center gap-2">
                              <span className="text-xs font-black text-white">⭐ PRIsME</span>
                              <span className="text-xs font-black text-white">•</span>
                              <span className="text-xs font-black text-white">CADRE BZ</span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-3">
                                <div className="bg-white/30 backdrop-blur-sm p-2 rounded-lg">
                                  <Target className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                  <h3 className="text-xl font-black text-white">2. TACTIQUE</h3>
                                  <p className="text-sm text-purple-100">Planification • Structuration • Exécution organisée</p>
                                </div>
                              </div>
                              <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                                <span className="text-xs font-bold text-white">NIVEAU 2 • HAUT DE GAMME</span>
                              </div>
                            </div>
                            <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-lg p-4">
                              <p className="text-xs font-semibold text-white mb-2">📋 Livrables PRO (Rapports & Dashboards ITIL/COBIT/NIST) :</p>
                              <div className="grid grid-cols-2 gap-2">
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Roadmap TI annuel (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan budgétaire TI annuel (COBIT)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan infrastructure TI (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Offre de service BZ (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Audit sécurité (NIST CSF 2.0)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Évaluation conformité Loi 25 (ISO)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan de formation TI (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Politique cybersécurité (NIST)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Spécifications d'exigences fonctionnelles (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Registre amélioration continue (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Registre risques TI (COBIT)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Dashboard maturité TI (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Rapport diagnostic TI (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan reprise activité PRA (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Matrice RACI processus (ITIL)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Rapport conformité NIST (NIST CSF 2.0)</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Plan de gestion changements (ITIL)</span>
                              </div>
                              <div className="mt-3 pt-3 border-t border-white/30">
                                <p className="text-xs text-purple-100 italic">
                                  💡 <strong className="text-white">Pourquoi ce niveau ?</strong> Les PME veulent du concret (livrables tangibles) mais avec une vision structurée. 
                                  Le niveau tactique offre le meilleur équilibre : <strong className="text-white">concret ET stratégique</strong>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Niveau 3 - OPÉRATIONNEL */}
                        <div className="w-full max-w-4xl">
                          <div className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 rounded-b-xl p-4 shadow-xl border-4 border-green-400 relative">
                            <div className="absolute -top-2 -right-2 bg-green-600 px-3 py-1 rounded-full border-2 border-white shadow-lg">
                              <span className="text-xs font-black text-white">💪 FORCE BZ</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="bg-white/30 backdrop-blur-sm p-2 rounded-lg">
                                  <Zap className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-black text-white">3. OPÉRATIONNEL</h3>
                                  <p className="text-xs text-green-100">Actions quotidiennes • Exécution • Support</p>
                                </div>
                              </div>
                              <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                                <span className="text-xs font-bold text-white">NIVEAU 3 • EXCELLENCE</span>
                              </div>
                            </div>
                            <div className="mt-3 bg-white/20 backdrop-blur-sm rounded-lg p-3">
                              <p className="text-xs font-semibold text-white mb-2">📋 Livrables typiques (Les "feuilles") :</p>
                              <div className="flex flex-wrap gap-2">
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Tickets support</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Installations</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Configurations</span>
                                <span className="bg-white/30 text-white text-xs px-2 py-1 rounded">Interventions ponctuelles</span>
                              </div>
                              <div className="mt-2 pt-2 border-t border-white/30">
                                <p className="text-xs text-green-100 font-semibold">
                                  ✅ <strong className="text-white">BZ excelle à ce niveau depuis des années</strong> - C'est notre force !
                                </p>
                                <p className="text-xs text-green-100 italic mt-1">
                                  ⚠️ Vendre uniquement à ce niveau = approche "feuilles" sans vision d'ensemble
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pourquoi cette profondeur ? */}
                    <div className="bg-white rounded-xl p-6 shadow-xl border border-gray-200 mb-4">
                      <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                        Pourquoi cette profondeur à 3 niveaux ?
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                          <h4 className="font-bold text-blue-900 mb-2 text-sm">🎯 PRIsME & Solutions d'Affaires = Niveaux 1 & 2 PRO</h4>
                          <p className="text-xs text-gray-700 leading-relaxed">
                            <strong>PRIsME</strong> et <strong>Solutions d'Affaires</strong> vont permettre à BZ de devenir <strong>autant sur la coche</strong> aux niveaux 
                            <strong>1 (Stratégique)</strong> et <strong>2 (Tactique)</strong> qu'ils le sont déjà au niveau <strong>3 (Opérationnel)</strong>. 
                            Nos livrables sont principalement des <strong>rapports et dashboards normés ITIL/COBIT/NIST</strong> (pas des outils opérationnels), 
                            car ils structurent et planifient sans entrer dans l'exécution quotidienne.
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                          <h4 className="font-bold text-green-900 mb-2 text-sm">💪 BZ excelle en OPÉRATIONNEL depuis des années</h4>
                          <p className="text-xs text-gray-700 leading-relaxed">
                            Le niveau <strong>opérationnel</strong> est notre <strong>force historique</strong>. BZ excelle dans l'exécution quotidienne, 
                            le support technique, les installations et configurations. <strong>PRIsME</strong> va maintenant nous permettre 
                            d'atteindre le <strong>même niveau d'excellence</strong> en stratégique et tactique.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Techniques de communication par niveau */}
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 shadow-xl border border-indigo-200 mb-4">
                      <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-indigo-600" />
                        Techniques de Communication par Niveau
                      </h3>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-4 shadow-md border-2 border-yellow-400 relative">
                          <div className="absolute -top-2 -right-2 bg-indigo-600 text-white px-2 py-1 rounded-full text-xs font-black">
                            ⭐ PRIsME
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="bg-yellow-500 text-white rounded-full p-2 min-w-[40px] h-10 flex items-center justify-center font-black text-sm">1</div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-1 text-sm">Niveau STRATÉGIQUE ⭐</h4>
                              <p className="text-xs text-gray-700 mb-2">Quand parler à ce niveau ?</p>
                              <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                                <li>Client demande une vision long terme (3-5 ans)</li>
                                <li>Besoin de structurer la direction TI</li>
                                <li>Décisions majeures à prendre (investissements importants)</li>
                                <li>Alignement TI avec stratégie d'affaires</li>
                              </ul>
                              <p className="text-xs text-yellow-700 font-semibold mt-2">
                                💡 Livrables clés : Plan Directeur TI (ITIL), Stratégie TI (COBIT), Vision 3-5 ans (ITIL), Cadre d'objectifs stratégiques (COBIT)
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 shadow-md border-2 border-purple-400 relative">
                          <div className="absolute -top-2 -right-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-black">
                            ⭐ PRIsME • BZ
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="bg-purple-600 text-white rounded-full p-2 min-w-[40px] h-10 flex items-center justify-center font-black text-sm">2</div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-1 text-sm">Niveau TACTIQUE ⭐</h4>
                              <p className="text-xs text-gray-700 mb-2">Quand parler à ce niveau ? (Notre spécialité)</p>
                              <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                                <li>Client veut du concret MAIS structuré (rapports, dashboards, plans)</li>
                                <li>Besoin de planification annuelle (roadmap, budget)</li>
                                <li>Structuration de processus (politiques, procédures)</li>
                                <li>Évaluation et audit (sécurité, conformité, maturité)</li>
                                <li>Formation et gouvernance (comité TI, plans de formation)</li>
                              </ul>
                              <p className="text-xs text-purple-700 font-semibold mt-2">
                                💡 Livrables clés : Roadmap TI annuel (ITIL), Plan budgétaire TI (COBIT), Audit sécurité (NIST), Plan infrastructure (ITIL), 
                                Offre de service BZ (ITIL), Politique cybersécurité (NIST), Plan formation (ITIL), Dashboard maturité (ITIL)
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 shadow-md border-2 border-green-400 relative">
                          <div className="absolute -top-2 -right-2 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-black">
                            💪 FORCE BZ
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="bg-green-500 text-white rounded-full p-2 min-w-[40px] h-10 flex items-center justify-center font-black text-sm">3</div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-1 text-sm">Niveau OPÉRATIONNEL 💪</h4>
                              <p className="text-xs text-gray-700 mb-2">Quand parler à ce niveau ? (Notre force depuis des années)</p>
                              <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                                <li>Client demande une intervention immédiate</li>
                                <li>Problème technique à résoudre (ticket support)</li>
                                <li>Installation ou configuration ponctuelle</li>
                                <li>Support quotidien</li>
                              </ul>
                              <p className="text-xs text-green-700 font-semibold mt-2">
                                💡 Livrables clés : Tickets résolus, Installations, Configurations
                              </p>
                              <p className="text-xs text-green-600 font-semibold mt-2">
                                ✅ <strong>BZ excelle à ce niveau depuis des années</strong> - C'est notre force historique !
                              </p>
                              <p className="text-xs text-red-600 font-semibold mt-1 italic">
                                ⚠️ Attention : Vendre uniquement à ce niveau = approche "feuilles" sans vision stratégique
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Message clé */}
                    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl p-6 shadow-xl text-white">
                      <div className="flex items-start gap-4">
                        <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                          <Target className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-black mb-2">Message Clé</h3>
                          <p className="text-sm leading-relaxed mb-3">
                            <strong>BZ excelle en OPÉRATIONNEL depuis des années</strong> - c'est notre force historique. 
                            <strong>PRIsME</strong> et <strong>Solutions d'Affaires</strong> vont maintenant permettre à BZ de devenir 
                            <strong>autant sur la coche</strong> aux niveaux <strong>1 (Stratégique)</strong> et <strong>2 (Tactique)</strong> 
                            qu'ils le sont déjà au niveau <strong>3 (Opérationnel)</strong>.
                          </p>
                          <p className="text-sm leading-relaxed mb-3">
                            Le <strong>Cadre Référentiel BZ</strong> touche aux niveaux <strong>1 ET 2</strong> car c'est là 
                            que nous offrons le meilleur équilibre : <strong>concret ET structuré</strong>, 
                            répondant aux besoins des PME qui veulent du tangible mais avec une vision claire.
                          </p>
                          <p className="text-sm leading-relaxed">
                            Peu importe le niveau, l'important c'est de <strong>satisfaire le client</strong> avec des 
                            <strong>livrables tangibles normés ITIL/COBIT/NIST</strong>. 
                            BZ va maintenant exceller aux <strong>3 niveaux</strong> grâce à PRIsME.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'ebauche-piliers' && (
                  <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 p-6">
                    <EbaucheCadreBZPiliersTab />
                  </div>
                )}

                {activeTab === 'ebauche-transversales' && (
                  <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 p-6">
                    <EbaucheCadreBZTransversalesTab />
                  </div>
                )}

                {/* RAISON D'ÊTRE */}
                {activeTab === 'raison' && (
                  <div className="space-y-2">
                    {/* Titre principal */}
                    <div className="text-center mb-4">
                      <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 p-2 rounded-lg mb-1">
                        <BookOpen className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-lg font-black text-gray-900 mb-1">
                        Pourquoi le Cadre Référentiel BZ?
                      </h2>
                      <p className="text-sm text-gray-600 max-w-3xl mx-auto">
                        La méthodologie éprouvée pour évaluer, structurer et faire progresser la maturité TI de vos clients
                      </p>
                    </div>

                    {/* Définition */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-2">
                      <div className="flex items-start gap-2 mb-2">
                        <div className="bg-blue-600 rounded-full p-1.5">
                          <Target className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-gray-900 mb-1">Définition</h3>
                          <p className="text-gray-700 leading-relaxed text-sm">
                            Le <strong>Cadre Référentiel BZ</strong> est une <strong>méthodologie holistique</strong> qui permet d'évaluer la maturité technologique et organisationnelle d'une entreprise à travers <strong>7 piliers stratégiques</strong> et <strong>65 tactiques concrètes</strong>. 
                            Il sert de <strong>boussole décisionnelle</strong> pour prioriser les investissements TI et créer des roadmaps basés sur la valeur réelle.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Mission et Valeur */}
                    <div>
                      <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Mission et Valeur Ajoutée
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white border-2 border-purple-200 rounded-lg p-4 hover:shadow-xl transition-shadow">
                          <div className="bg-purple-100 w-8 h-8 rounded-lg flex items-center justify-center mb-2">
                            <Target className="w-4 h-4 text-purple-600" />
                          </div>
                          <h4 className="font-bold text-sm mb-1.5 text-gray-900">Évaluer la maturité TI avec précision</h4>
                          <p className="text-gray-700 text-xs leading-relaxed">
                            Diagnostiquer <strong>où en est le client</strong> (As-Is), <strong>d'où il vient</strong> (As-Was) et <strong>où il doit aller</strong> (Target) à travers 7 dimensions critiques. Obtenir une <strong>vue 360° objective</strong>.
                          </p>
                        </div>

                        <div className="bg-white border-2 border-blue-200 rounded-lg p-2 hover:shadow-lg transition-shadow">
                          <div className="bg-blue-100 w-8 h-8 rounded-lg flex items-center justify-center mb-2">
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                          </div>
                          <h4 className="font-bold text-sm mb-1.5 text-gray-900">Visualiser les écarts et opportunités</h4>
                          <p className="text-gray-700 text-xs leading-relaxed">
                            Utiliser des <strong>spider charts visuels</strong> pour communiquer facilement les forces, faiblesses et écarts de maturité. Prioriser les <strong>quick wins</strong> vs investissements structurants.
                          </p>
                        </div>

                        <div className="bg-white border-2 border-green-200 rounded-lg p-4 hover:shadow-xl transition-shadow">
                          <div className="bg-green-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                            <Calendar className="w-7 h-7 text-green-600" />
                          </div>
                          <h4 className="font-bold text-lg mb-3 text-gray-900">Créer des roadmaps structurés</h4>
                          <p className="text-gray-700 text-xs leading-relaxed">
                            Transformer les écarts en <strong>plans d'action concrets</strong> (Q1, Q2, Q3, Q4) avec priorisation, budgets, responsables. Du diagnostic à l'<strong>exécution mesurable</strong>.
                          </p>
                        </div>

                        <div className="bg-white border-2 border-orange-200 rounded-lg p-4 hover:shadow-xl transition-shadow">
                          <div className="bg-orange-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                            <Award className="w-7 h-7 text-orange-600" />
                          </div>
                          <h4 className="font-bold text-lg mb-3 text-gray-900">Aligner client et experts BZ</h4>
                          <p className="text-gray-700 text-xs leading-relaxed">
                            Créer un <strong>langage commun</strong> entre parties prenantes business et techniques. Éliminer les incompréhensions et <strong>justifier chaque dollar investi</strong> avec clarté.
                          </p>
                        </div>

                        <div className="bg-white border-2 border-pink-200 rounded-lg p-4 hover:shadow-xl transition-shadow">
                          <div className="bg-pink-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                            <Users className="w-7 h-7 text-pink-600" />
                          </div>
                          <h4 className="font-bold text-lg mb-3 text-gray-900">Éduquer et autonomiser le client</h4>
                          <p className="text-gray-700 text-xs leading-relaxed">
                            Transférer les <strong>connaissances TI</strong> au client via des explications claires, visuels pédagogiques, exemples concrets. Le client devient <strong>décideur éclairé</strong>.
                          </p>
                        </div>

                        <div className="bg-white border-2 border-indigo-200 rounded-lg p-4 hover:shadow-xl transition-shadow">
                          <div className="bg-indigo-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                            <TrendingUp className="w-7 h-7 text-indigo-600" />
                          </div>
                          <h4 className="font-bold text-lg mb-3 text-gray-900">Mesurer le progrès et le ROI</h4>
                          <p className="text-gray-700 text-xs leading-relaxed">
                            Suivre l'<strong>évolution de la maturité</strong> trimestriellement, quantifier les gains (efficacité, réduction risques, satisfaction), prouver la <strong>valeur de BZ</strong>.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Les 7 Piliers Expliqués */}
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-xl p-4">
                      <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
                        <Database className="w-7 h-7 text-purple-600" />
                        Les 7 Piliers Fondamentaux
                      </h3>
                      <p className="text-gray-700 mb-3 text-sm">
                        Le cadre BZ structure la maturité TI en <strong>7 piliers interdépendants</strong>, chacun représentant une dimension critique de l'excellence technologique et organisationnelle:
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {piliers.map((pilier) => {
                          return (
                            <div key={pilier.id} className="bg-white rounded-xl p-2 border border-purple-200">
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className={`bg-gradient-to-br ${pilier.couleur} w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs`}>
                                  {pilier.id}
                                </div>
                                <h4 className="font-bold text-gray-900 text-sm">{pilier.nom}</h4>
                              </div>
                              <p className="text-xs text-gray-700 leading-relaxed">{pilier.description}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Standards et Méthodologies */}
                    <div>
                      <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
                        <Award className="w-7 h-7 text-blue-600" />
                        Standards et Méthodologies Intégrés
                      </h3>
                      <p className="text-gray-700 mb-3 text-sm">
                        Le Cadre BZ n'est pas inventé dans le vide. Il s'appuie sur les <strong>meilleures pratiques mondiales reconnues</strong> et les adapte à la réalité des PME québécoises:
                      </p>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                          <div className="text-2xl mb-1.5">📘</div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900">ITIL 4</h4>
                          <p className="text-sm text-gray-700 mb-2">Gestion services TI, amélioration continue</p>
                          <div className="text-xs text-blue-600 font-semibold">Standard mondial</div>
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                          <div className="text-4xl mb-3">🔐</div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900">COBIT 2019</h4>
                          <p className="text-sm text-gray-700 mb-2">Gouvernance TI/affaires, audits</p>
                          <div className="text-xs text-blue-600 font-semibold">Framework ISACA</div>
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                          <div className="text-4xl mb-3">🛡️</div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900">NIST Cyber</h4>
                          <p className="text-sm text-gray-700 mb-2">Sécurité, gestion risques</p>
                          <div className="text-xs text-blue-600 font-semibold">Norme US</div>
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                          <div className="text-4xl mb-3">📊</div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900">ISO 27001</h4>
                          <p className="text-sm text-gray-700 mb-2">SMSI, certification sécurité</p>
                          <div className="text-xs text-blue-600 font-semibold">ISO international</div>
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                          <div className="text-4xl mb-3">🎯</div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900">PMBOK (PMI)</h4>
                          <p className="text-sm text-gray-700 mb-2">Gestion projets, portefeuille</p>
                          <div className="text-xs text-blue-600 font-semibold">PMI</div>
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                          <div className="text-4xl mb-3">⚡</div>
                          <h4 className="font-bold text-lg mb-2 text-gray-900">Agile/SAFe</h4>
                          <p className="text-sm text-gray-700 mb-2">Agilité, livraison itérative</p>
                          <div className="text-xs text-blue-600 font-semibold">Scaled Agile</div>
                        </div>
                      </div>
                    </div>

                    {/* Comment l'utiliser */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                        <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
                          <Zap className="w-6 h-6 text-orange-600" />
                          Comment Utiliser le Cadre
                        </h3>
                        <ul className="space-y-3 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div><strong>1. Diagnostic initial:</strong> Évaluer As-Was et As-Is avec le client</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div><strong>2. Définir Target:</strong> Co-créer la maturité cible par pilier</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div><strong>3. Visualiser écarts:</strong> Spider charts pour communication</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div><strong>4. Prioriser tactiques:</strong> Quick wins vs structurant</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div><strong>5. Roadmap trimestrielle:</strong> Plan action Q1-Q4</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div><strong>6. Suivi et ajustements:</strong> Réévaluer chaque trimestre</div>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                        <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
                          <Crown className="w-6 h-6 text-purple-600" />
                          Qui Utilise le Cadre?
                        </h3>
                        <ul className="space-y-3 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div><strong>Prime/Directeur TI BZ:</strong> Pilotage stratégique, diagnostic client</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div><strong>Directeur Compte BZ:</strong> Alignement affaires, roadmap commercial</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div><strong>Client (DG/VP):</strong> Compréhension maturité, validation budgets</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div><strong>Comité TI:</strong> Priorisation investissements, suivi KPIs</div>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div><strong>Experts BZ:</strong> Identification tactiques, livrables techniques</div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Citation inspirante */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-4 text-center">
                      <div className="text-5xl mb-4">🎯</div>
                      <blockquote className="text-base font-bold italic mb-2">
                        "Le Cadre Référentiel BZ transforme l'évaluation TI d'un exercice abstrait en roadmap actionnable qui génère de la valeur mesurable."
                      </blockquote>
                      <p className="text-blue-100">— Philosophie BZ: De la vision à l'exécution</p>
                    </div>
                  </div>
                )}

                {/* VUE D'ENSEMBLE */}
                {activeTab === 'overview' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                      <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                        <Zap className="w-8 h-8 text-blue-600" />
                        Vue d'ensemble - {clientMockup.nom}
                      </h2>

                      <div className="grid grid-cols-3 gap-6 mb-8">
                        {piliers.slice(0, 7).map((pilier, i) => {
                          const Icon = iconMap[pilier.icon];
                          const progression = ((pilier.asIs - pilier.asWas) / (pilier.target - pilier.asWas) * 100);
                          return (
                            <motion.div
                              key={pilier.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: i * 0.1 }}
                              className="bg-gradient-to-br from-white to-gray-50 p-4 rounded-lg shadow-lg border border-gray-200 hover:shadow-2xl transition-all cursor-pointer"
                              onClick={() => setActiveTab('piliers')}
                            >
                              <div className="flex items-start justify-between mb-4">
                                <div className={`bg-gradient-to-br ${pilier.couleur} p-3 rounded-xl`}>
                                  <Icon className="w-6 h-6 text-white" />
                                </div>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                                  {pilier.tactiquesActives}/{pilier.nombreTactiques}
                                </span>
                              </div>
                              <h3 className="font-bold text-gray-900 mb-2 text-sm leading-tight">{pilier.nom}</h3>
                              <div className="space-y-2">
                                <div className="flex justify-between text-xs text-gray-600">
                                  <span>Maturité</span>
                                  <span className="font-bold">{pilier.asIs}/5</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full bg-gradient-to-r ${pilier.couleur} transition-all duration-500`}
                                    style={{ width: `${(pilier.asIs / 5) * 100}%` }}
                                  />
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">Progression: {progression.toFixed(0)}%</span>
                                  <span className="text-green-600 font-semibold">+{(pilier.asIs - pilier.asWas).toFixed(1)}</span>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Timeline visuelle */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Évolution maturité</h3>
                        <div className="flex items-center justify-between">
                          <div className="text-center">
                            <div className="bg-gray-200 w-20 h-20 rounded-full flex items-center justify-center mb-2">
                              <span className="text-lg font-black text-gray-600">{(piliers.reduce((sum, p) => sum + p.asWas, 0) / piliers.length).toFixed(1)}</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-700">As-Was</p>
                            <p className="text-xs text-gray-500">{clientMockup.dateEvaluation}</p>
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="h-2 bg-gradient-to-r from-gray-300 via-blue-500 to-green-500 rounded-full" />
                          </div>
                          <div className="text-center">
                            <div className="bg-blue-500 w-20 h-20 rounded-full flex items-center justify-center mb-2 animate-pulse">
                              <span className="text-lg font-black text-white">{statsGlobales.maturiteGlobale}</span>
                            </div>
                            <p className="text-sm font-semibold text-blue-700">As-Is</p>
                            <p className="text-xs text-blue-600">Aujourd'hui</p>
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="h-2 bg-gradient-to-r from-blue-300 to-green-300 rounded-full opacity-50" />
                          </div>
                          <div className="text-center">
                            <div className="bg-green-500 w-20 h-20 rounded-full flex items-center justify-center mb-2">
                              <span className="text-lg font-black text-white">{(piliers.reduce((sum, p) => sum + p.target, 0) / piliers.length).toFixed(1)}</span>
                            </div>
                            <p className="text-sm font-semibold text-green-700">Target</p>
                            <p className="text-xs text-green-600">{clientMockup.delaiObjectif}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Prochaines actions */}
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <Clock className="w-7 h-7 text-orange-600" />
                        Prochaines priorités
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {roadmap[0].priorites.map((priorite, i) => (
                          <div key={i} className="flex items-start gap-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                            <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                            <span className="text-gray-700 font-medium">{priorite}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* PILIERS DÉTAILLÉS */}
                {activeTab === 'piliers' && (
                  <div className="space-y-2">
                    {piliers.map((pilier) => {
                      const Icon = iconMap[pilier.icon];
                      const tactiquesPilier = tactiques.filter(t => t.pilier === pilier.id);
                      const progression = ((pilier.asIs - pilier.asWas) / (pilier.target - pilier.asWas) * 100);
                      
                      return (
                        <motion.div
                          key={pilier.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200"
                        >
                          <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center gap-4">
                              <div className={`bg-gradient-to-br ${pilier.couleur} p-4 rounded-2xl`}>
                                <Icon className="w-8 h-8 text-white" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-base font-bold text-gray-900">{pilier.nom}</h3>
                                <p className="text-gray-600 mt-1">{pilier.description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Tactiques actives</p>
                              <p className="text-xl font-black text-gray-900">{pilier.tactiquesActives}/{pilier.nombreTactiques}</p>
                            </div>
                          </div>

                          {/* Description complète enrichie */}
                          {pilier.descriptionComplete && (
                            <div className="mb-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                              <p className="text-gray-800 text-sm leading-relaxed">{pilier.descriptionComplete}</p>
                            </div>
                          )}

                          {/* Objectif stratégique */}
                          {pilier.objectifStrategique && (
                            <div className="mb-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                              <h4 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                                <Target className="w-5 h-5" />
                                Objectif Stratégique
                              </h4>
                              <p className="text-gray-800 text-sm font-medium">{pilier.objectifStrategique}</p>
                            </div>
                          )}

                          {/* Métriques pilier */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                            <div className="bg-gray-50 p-4 rounded-xl">
                              <p className="text-sm text-gray-600 mb-1">As-Was</p>
                              <p className="text-2xl font-bold text-gray-700">{pilier.asWas}/5</p>
                            </div>
                            <div className="bg-blue-50 p-4 rounded-xl">
                              <p className="text-sm text-blue-600 mb-1">As-Is</p>
                              <p className="text-2xl font-bold text-blue-700">{pilier.asIs}/5</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-xl">
                              <p className="text-sm text-green-600 mb-1">Target</p>
                              <p className="text-2xl font-bold text-green-700">{pilier.target}/5</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-xl">
                              <p className="text-sm text-purple-600 mb-1">Progression</p>
                              <p className="text-2xl font-bold text-purple-700">{progression.toFixed(0)}%</p>
                            </div>
                          </div>

                          {/* Bénéfices clés et Risques */}
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            {pilier.beneficesCles && pilier.beneficesCles.length > 0 && (
                              <div className="bg-green-50 p-5 rounded-xl border border-green-200">
                                <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                  <CheckCircle className="w-5 h-5" />
                                  Bénéfices Clés
                                </h4>
                                <ul className="space-y-2">
                                  {pilier.beneficesCles.map((benefice, i) => (
                                    <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                                      <span className="text-green-600 mt-0.5">✓</span>
                                      <span>{benefice}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {pilier.risquesSiAbsent && pilier.risquesSiAbsent.length > 0 && (
                              <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                                <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                                  <X className="w-5 h-5" />
                                  Risques si Négligé
                                </h4>
                                <ul className="space-y-2">
                                  {pilier.risquesSiAbsent.map((risque, i) => (
                                    <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                                      <span className="text-red-600 mt-0.5">⚠</span>
                                      <span>{risque}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {/* Liste tactiques pilier */}
                          <div className="space-y-2">
                            <h4 className="font-bold text-gray-900 mb-3">Tactiques ({tactiquesPilier.length})</h4>
                            {tactiquesPilier.map((tactique, i) => (
                              <div key={tactique.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <span className="text-sm font-bold text-gray-500 w-8">{i + 1}</span>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900 text-sm">{tactique.nom}</p>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  tactique.statut === 'Fait' ? 'bg-green-100 text-green-700' :
                                  tactique.statut === 'En cours' ? 'bg-blue-100 text-blue-700' :
                                  tactique.statut === 'À faire' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {tactique.statut}
                                </span>
                                <div className="flex gap-1">
                                  <div className={`w-2 h-2 rounded-full ${tactique.asWas >= 3 ? 'bg-gray-400' : 'bg-gray-200'}`} />
                                  <div className={`w-2 h-2 rounded-full ${tactique.asIs >= 3 ? 'bg-blue-500' : 'bg-gray-200'}`} />
                                  <div className={`w-2 h-2 rounded-full ${tactique.target >= 3 ? 'bg-green-500' : 'bg-gray-200'}`} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {/* TACTIQUES */}
                {activeTab === 'tactiques' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200 mb-4">
                      <h2 className="text-base font-bold text-gray-900 mb-2">Filtres et statistiques</h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                        {[
                          { label: 'Toutes', count: tactiques.length, color: 'gray' },
                          { label: 'Fait', count: tactiques.filter(t => t.statut === 'Fait').length, color: 'green' },
                          { label: 'En cours', count: tactiques.filter(t => t.statut === 'En cours').length, color: 'blue' },
                          { label: 'À faire', count: tactiques.filter(t => t.statut === 'À faire').length, color: 'orange' },
                          { label: 'Optionnel', count: tactiques.filter(t => t.statut === 'Optionnel').length, color: 'gray' }
                        ].map((filter) => (
                          <div key={filter.label} className={`bg-${filter.color}-50 p-4 rounded-xl text-center`}>
                            <p className="text-xl font-black text-${filter.color}-700">{filter.count}</p>
                            <p className="text-sm text-${filter.color}-600 font-semibold">{filter.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {piliers.map((pilier) => {
                      const Icon = iconMap[pilier.icon];
                      const tactiquesPilier = tactiques.filter(t => t.pilier === pilier.id);
                      
                      return (
                        <div key={pilier.id} className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
                          <div className="flex items-center gap-3 mb-4">
                            <div className={`bg-gradient-to-br ${pilier.couleur} p-3 rounded-xl`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-sm font-bold text-gray-900">{pilier.nom}</h3>
                            <span className="ml-auto px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-bold">
                              {tactiquesPilier.length} tactiques
                            </span>
                          </div>
                          
                          <div className="space-y-3">
                            {tactiquesPilier.map((tactique) => (
                              <div key={tactique.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-gray-50">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-1 rounded">{tactique.id}</span>
                                      <h4 className="font-bold text-gray-900 text-base">{tactique.nom}</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3">{tactique.description}</p>
                                  </div>
                                  <div className="flex flex-col items-end gap-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                                      tactique.statut === 'Fait' ? 'bg-green-100 text-green-700' :
                                      tactique.statut === 'En cours' ? 'bg-blue-100 text-blue-700' :
                                      tactique.statut === 'À faire' ? 'bg-orange-100 text-orange-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {tactique.statut}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                                      tactique.priorite === 'Haute' ? 'bg-red-100 text-red-700' :
                                      tactique.priorite === 'Moyenne' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {tactique.priorite}
                                    </span>
                                  </div>
                                </div>

                                {/* Description détaillée enrichie */}
                                {tactique.descriptionDetaillee && (
                                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <h5 className="text-xs font-bold text-blue-900 mb-2">📋 Description Détaillée</h5>
                                    <p className="text-xs text-gray-700 leading-relaxed">{tactique.descriptionDetaillee}</p>
                                  </div>
                                )}

                                {/* Cas d'usage concret */}
                                {tactique.casUsage && (
                                  <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <h5 className="text-xs font-bold text-purple-900 mb-2">💡 Cas d'Usage Concret</h5>
                                    <p className="text-xs text-gray-700 leading-relaxed italic">{tactique.casUsage}</p>
                                  </div>
                                )}

                                {/* Impact métier */}
                                {tactique.impactMetier && (
                                  <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                                    <h5 className="text-xs font-bold text-green-900 mb-2">📊 Impact Métier</h5>
                                    <p className="text-xs text-green-900 font-semibold">{tactique.impactMetier}</p>
                                  </div>
                                )}

                                {/* Livrables visuels tangibles */}
                                {tactique.livrablesVisuels && tactique.livrablesVisuels.length > 0 && (
                                  <div className="mb-4">
                                    <h5 className="text-xs font-bold text-gray-900 mb-3">🎯 Livrables Visuels Tangibles</h5>
                                    <div className="grid grid-cols-3 gap-3">
                                      {tactique.livrablesVisuels.map((livrable, i) => (
                                        <div key={i} className="bg-white rounded-lg overflow-hidden border border-gray-300 shadow-md hover:shadow-xl transition-shadow">
                                          <div className="h-32 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                                            <img 
                                              src={livrable.imageUrl} 
                                              alt={livrable.nom}
                                              className="w-full h-full object-cover"
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=' + livrable.type;
                                              }}
                                            />
                                          </div>
                                          <div className="p-3">
                                            <div className="flex items-center gap-2 mb-1">
                                              <FileText className="w-3 h-3 text-blue-600" />
                                              <span className="text-xs font-bold text-gray-900">{livrable.nom}</span>
                                            </div>
                                            <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded mb-2">
                                              {livrable.type}
                                            </span>
                                            <p className="text-xs text-gray-600 leading-tight">{livrable.description}</p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* RACI Mini */}
                                <div className="bg-gray-100 p-3 rounded-lg mb-3">
                                  <h5 className="text-xs font-bold text-gray-900 mb-2">RACI - Responsabilités</h5>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <p className="text-gray-500 font-semibold mb-1">R - Responsable</p>
                                      <p className="text-gray-700">{tactique.raci.responsible}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 font-semibold mb-1">A - Imputable</p>
                                      <p className="text-gray-700">{tactique.raci.accountable}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 font-semibold mb-1">C - Consulté</p>
                                      <p className="text-gray-700">{tactique.raci.consulted}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 font-semibold mb-1">I - Informé</p>
                                      <p className="text-gray-700">{tactique.raci.informed}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Scores */}
                                <div className="flex items-center gap-4 mb-3">
                                  <div className="flex-1 bg-gray-100 rounded-lg p-3">
                                    <div className="flex justify-between items-center mb-2">
                                      <span className="text-xs text-gray-600">As-Was: {tactique.asWas}</span>
                                      <span className="text-xs text-blue-600 font-bold">As-Is: {tactique.asIs}</span>
                                      <span className="text-xs text-green-600 font-bold">Target: {tactique.target}</span>
                                    </div>
                                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-gradient-to-r from-blue-500 to-green-500"
                                        style={{ width: `${(tactique.asIs / tactique.target) * 100}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* KPI */}
                                {tactique.kpi && (
                                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                                    <p className="text-xs text-indigo-900"><strong>📈 KPI:</strong> {tactique.kpi}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* LIVRABLES TANGIBLES */}
                {activeTab === 'livrables' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200 mb-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-3 rounded-xl">
                          <FileSpreadsheet className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-base font-black text-gray-900">Livrables Tangibles par Tactique</h2>
                          <p className="text-sm text-gray-600 mt-1">
                            Liste exhaustive des livrables professionnels (documents ITIL/COBIT/NIST, outils, dashboards) associés à chaque tactique du Cadre BZ
                          </p>
                        </div>
                      </div>
                      
                      {/* Statistiques */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-xl text-center">
                          <p className="text-xl font-black text-blue-700">{livrablesTangiblesParTactique.length}</p>
                          <p className="text-sm text-blue-600 font-semibold">Livrables totaux</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl text-center">
                          <p className="text-xl font-black text-green-700">
                            {livrablesTangiblesParTactique.filter(l => l.mentionneSolutionsAffaires).length}
                          </p>
                          <p className="text-sm text-green-600 font-semibold">Mentionnés Solutions d'Affaires</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-xl text-center">
                          <p className="text-xl font-black text-purple-700">
                            {new Set(livrablesTangiblesParTactique.map(l => l.standard)).size - 1}
                          </p>
                          <p className="text-sm text-purple-600 font-semibold">Standards couverts</p>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-xl text-center">
                          <p className="text-xl font-black text-orange-700">
                            {new Set(livrablesTangiblesParTactique.map(l => l.type)).size}
                          </p>
                          <p className="text-sm text-orange-600 font-semibold">Types de livrables</p>
                        </div>
                      </div>
                    </div>

                    {/* Tableau Excel-like */}
                    <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gradient-to-r from-gray-800 to-gray-900 text-white sticky top-0 z-10">
                            <tr>
                              <th className="px-2 py-1.5 text-left text-xs font-bold border-r border-gray-700 min-w-[60px]">Pilier</th>
                              <th className="px-2 py-1.5 text-left text-xs font-bold border-r border-gray-700 min-w-[80px]">Tactique</th>
                              <th className="px-2 py-1.5 text-left text-xs font-bold border-r border-gray-700 min-w-[150px]">Livrable Tangible</th>
                              <th className="px-2 py-1.5 text-left text-xs font-bold border-r border-gray-700 min-w-[200px]">Description</th>
                              <th className="px-2 py-1.5 text-center text-xs font-bold border-r border-gray-700 min-w-[80px]">Type</th>
                              <th className="px-2 py-1.5 text-center text-xs font-bold border-r border-gray-700 min-w-[80px]">Standard</th>
                              <th className="px-2 py-1.5 text-center text-xs font-bold border-r border-gray-700 min-w-[90px]">Profondeur</th>
                              <th className="px-4 py-3 text-center font-bold min-w-[80px]">
                                <div className="flex items-center justify-center gap-1">
                                  <Briefcase className="w-4 h-4" />
                                  <span>Sol. Aff.</span>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {livrablesTangiblesParTactique.map((livrable, index) => {
                              const profondeurColors = {
                                'Humaine': 'bg-blue-100 text-blue-800 border-blue-300',
                                'Procédurale': 'bg-purple-100 text-purple-800 border-purple-300',
                                'Technologique': 'bg-green-100 text-green-800 border-green-300',
                                'Mixte': 'bg-orange-100 text-orange-800 border-orange-300'
                              };
                              
                              const typeColors = {
                                'Document': 'bg-gray-100 text-gray-800',
                                'Outil': 'bg-indigo-100 text-indigo-800',
                                'Dashboard': 'bg-cyan-100 text-cyan-800',
                                'Rapport': 'bg-blue-100 text-blue-800',
                                'Matrice': 'bg-pink-100 text-pink-800',
                                'Présentation': 'bg-yellow-100 text-yellow-800',
                                'Processus': 'bg-emerald-100 text-emerald-800'
                              };
                              
                              const standardColors = {
                                'ITIL': 'bg-blue-500 text-white',
                                'COBIT': 'bg-purple-500 text-white',
                                'NIST': 'bg-green-500 text-white',
                                'ISO': 'bg-red-500 text-white',
                                'PMBOK': 'bg-orange-500 text-white',
                                'RACI': 'bg-indigo-500 text-white',
                                'Agile': 'bg-yellow-500 text-white',
                                'Scrum': 'bg-cyan-500 text-white',
                                'Kanban': 'bg-teal-500 text-white',
                                'DevOps': 'bg-pink-500 text-white',
                                'BZ': 'bg-violet-500 text-white',
                                'Formation': 'bg-amber-500 text-white',
                                'Autre': 'bg-gray-500 text-white'
                              };
                              
                              return (
                                <tr 
                                  key={livrable.id} 
                                  className={`hover:bg-gray-50 transition-colors ${
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                                  }`}
                                >
                                  <td className="px-2 py-1.5 border-r border-gray-200 text-xs">
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold text-gray-700">P{livrable.pilierId}</span>
                                      <span className="text-xs font-semibold text-gray-800">
                                        {(() => {
                                          const pilierAbrev: Record<number, string> = {
                                            1: 'STG', // Stratégie & Gouvernance TI
                                            2: 'ACT', // Gestion des Actifs & Gouvernance
                                            3: 'POR', // Gouvernance de Portefeuille
                                            4: 'SER', // Gestion des Services TI
                                            5: 'CYB', // Gouvernance Cybersécurité
                                            6: 'AME', // Amélioration Continue
                                            7: 'INN'  // Innovation & IA
                                          };
                                          return pilierAbrev[livrable.pilierId] || 'N/A';
                                        })()}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-1.5 border-r border-gray-200 text-xs">
                                    <div>
                                      <div className="font-mono text-xs text-gray-500 mb-1">{livrable.tactiqueId}</div>
                                      <div className="text-xs text-gray-700 line-clamp-2">{livrable.tactiqueNom}</div>
                                    </div>
                                  </td>
                                  <td className="px-2 py-1.5 border-r border-gray-200 text-xs">
                                    <div className="font-semibold text-gray-900">{livrable.livrableNom}</div>
                                  </td>
                                  <td className="px-2 py-1.5 border-r border-gray-200 text-xs">
                                    <div className="text-gray-700 text-xs leading-relaxed line-clamp-3">
                                      {livrable.description}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 border-r border-gray-200 text-center">
                                    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                      typeColors[livrable.type] || 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {livrable.type}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 border-r border-gray-200 text-center">
                                    <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${
                                      standardColors[livrable.standard] || 'bg-gray-500 text-white'
                                    }`}>
                                      {livrable.standard}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 border-r border-gray-200 text-center">
                                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold border ${
                                      profondeurColors[livrable.profondeur] || 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {livrable.profondeur}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {livrable.mentionneSolutionsAffaires ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-6 h-6 bg-green-500 rounded border-2 border-green-600 flex items-center justify-center">
                                          <CheckCircle className="w-4 h-4 text-white" />
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-center">
                                        <div className="w-6 h-6 bg-gray-200 rounded border-2 border-gray-300"></div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Légende */}
                      <div className="bg-gray-50 border-t border-gray-200 p-4">
                        {(() => {
                          const standardColorsLegend: Record<string, string> = {
                            'ITIL': 'bg-blue-500',
                            'COBIT': 'bg-purple-500',
                            'NIST': 'bg-green-500',
                            'ISO': 'bg-red-500',
                            'PMBOK': 'bg-orange-500',
                            'RACI': 'bg-indigo-500',
                            'Agile': 'bg-yellow-500',
                            'Scrum': 'bg-cyan-500',
                            'Kanban': 'bg-teal-500',
                            'DevOps': 'bg-pink-500',
                            'BZ': 'bg-violet-500',
                            'Formation': 'bg-amber-500',
                            'Autre': 'bg-gray-500'
                          };
                          
                          const typeColorsLegend: Record<string, string> = {
                            'Document': 'bg-gray-100',
                            'Outil': 'bg-indigo-100',
                            'Dashboard': 'bg-cyan-100',
                            'Rapport': 'bg-blue-100',
                            'Matrice': 'bg-pink-100',
                            'Présentation': 'bg-yellow-100',
                            'Processus': 'bg-emerald-100'
                          };
                          
                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                              <div>
                                <p className="font-bold text-gray-700 mb-2">Profondeur (NIST-style):</p>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="w-4 h-4 rounded-full bg-blue-100 border border-blue-300"></span>
                                    <span>Humaine: Formation, sensibilisation, culture</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="w-4 h-4 rounded-full bg-purple-100 border border-purple-300"></span>
                                    <span>Procédurale: Processus, politiques, cadres</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="w-4 h-4 rounded-full bg-green-100 border border-green-300"></span>
                                    <span>Technologique: Outils, systèmes, infrastructure</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="w-4 h-4 rounded-full bg-orange-100 border border-orange-300"></span>
                                    <span>Mixte: Combinaison des trois</span>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <p className="font-bold text-gray-700 mb-2">Standards:</p>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                  {['ITIL', 'COBIT', 'NIST', 'ISO', 'PMBOK', 'RACI', 'Agile', 'Scrum', 'Kanban', 'DevOps', 'BZ', 'Formation', 'Autre'].map(std => (
                                    <div key={std} className="flex items-center gap-2">
                                      <span className={`w-3 h-3 rounded ${standardColorsLegend[std] || 'bg-gray-500'}`}></span>
                                      <span className="text-xs">{std}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="font-bold text-gray-700 mb-2">Types:</p>
                                <div className="space-y-1">
                                  {['Document', 'Outil', 'Dashboard', 'Rapport', 'Matrice', 'Présentation', 'Processus'].map(type => (
                                    <div key={type} className="flex items-center gap-2">
                                      <span className={`w-3 h-3 rounded ${typeColorsLegend[type] || 'bg-gray-100'}`}></span>
                                      <span>{type}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="font-bold text-gray-700 mb-2">Solutions d'Affaires:</p>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>Mentionné dans leur document</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-gray-200 rounded border border-gray-300"></div>
                                    <span>Non mentionné</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* SPIDER CHARTS */}
                {activeTab === 'spider' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                      <h2 className="text-sm font-bold text-gray-900 mb-2 text-center">Spider Chart Global - Maturité TI</h2>
                      <ResponsiveContainer width="100%" height={500}>
                        <RadarChart data={spiderData}>
                          <PolarGrid stroke="#e5e7eb" />
                          <PolarAngleAxis 
                            dataKey="pilier" 
                            tick={{ fill: '#374151', fontSize: 12, fontWeight: 'bold' }}
                          />
                          <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: '#6b7280' }} />
                          <Radar name="As-Was" dataKey="As-Was" stroke="#9ca3af" fill="#9ca3af" fillOpacity={0.3} strokeWidth={2} />
                          <Radar name="As-Is" dataKey="As-Is" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} strokeWidth={3} />
                          <Radar name="Target" dataKey="Target" stroke="#10b981" fill="#10b981" fillOpacity={0.3} strokeWidth={2} />
                          <Legend wrapperStyle={{ paddingTop: '20px' }} />
                          <Tooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                      
                      <div className="mt-8 grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-4 rounded-xl text-center">
                          <div className="w-4 h-4 bg-gray-400 rounded-full mx-auto mb-2" />
                          <p className="text-sm font-semibold text-gray-700">As-Was (Point de départ)</p>
                          <p className="text-base font-black text-gray-800 mt-0.5">{(piliers.reduce((sum, p) => sum + p.asWas, 0) / piliers.length).toFixed(1)}/5</p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-xl text-center">
                          <div className="w-4 h-4 bg-blue-500 rounded-full mx-auto mb-2" />
                          <p className="text-sm font-semibold text-blue-700">As-Is (État actuel)</p>
                          <p className="text-2xl font-black text-blue-800 mt-1">{statsGlobales.maturiteGlobale}/5</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl text-center">
                          <div className="w-4 h-4 bg-green-500 rounded-full mx-auto mb-2" />
                          <p className="text-sm font-semibold text-green-700">Target (Objectif)</p>
                          <p className="text-2xl font-black text-green-800 mt-1">{(piliers.reduce((sum, p) => sum + p.target, 0) / piliers.length).toFixed(1)}/5</p>
                        </div>
                      </div>
                    </div>

                    {/* Spider par pilier */}
                    <div className="grid grid-cols-2 gap-6">
                      {piliers.map((pilier) => {
                        const Icon = iconMap[pilier.icon];
                        const tactiquesPilier = tactiques.filter(t => t.pilier === pilier.id);
                        const spiderDataPilier = tactiquesPilier.map(t => ({
                          tactique: t.id.split('-')[1],
                          'As-Was': t.asWas,
                          'As-Is': t.asIs,
                          'Target': t.target
                        }));

                        return (
                          <div key={pilier.id} className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                            <div className="flex items-center gap-3 mb-4">
                              <div className={`bg-gradient-to-br ${pilier.couleur} p-3 rounded-xl`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <h3 className="text-lg font-bold text-gray-900">{pilier.nom}</h3>
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                              <RadarChart data={spiderDataPilier}>
                                <PolarGrid stroke="#e5e7eb" />
                                <PolarAngleAxis dataKey="tactique" tick={{ fill: '#6b7280', fontSize: 10 }} />
                                <PolarRadiusAxis angle={90} domain={[0, 5]} tick={false} />
                                <Radar name="As-Was" dataKey="As-Was" stroke="#9ca3af" fill="#9ca3af" fillOpacity={0.2} />
                                <Radar name="As-Is" dataKey="As-Is" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} strokeWidth={2} />
                                <Radar name="Target" dataKey="Target" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                              </RadarChart>
                            </ResponsiveContainer>
                            <div className="mt-4 flex justify-between text-sm">
                              <span className="text-gray-600">Maturité: <strong>{pilier.asIs}/5</strong></span>
                              <span className="text-green-600">Cible: <strong>{pilier.target}/5</strong></span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* KPI & MÉTRIQUES */}
                {activeTab === 'kpi' && (
                  <div className="space-y-2">
                    {piliers.map((pilier) => {
                      const Icon = iconMap[pilier.icon];
                      const kpi = kpiGlobaux[`pilier${pilier.id}` as keyof typeof kpiGlobaux];
                      
                      return (
                        <div key={pilier.id} className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`bg-gradient-to-br ${pilier.couleur} p-3 rounded-xl`}>
                              <Icon className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-base font-bold text-gray-900">{pilier.nom}</h3>
                              <p className="text-gray-600">{pilier.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Score</p>
                              <p className="text-2xl font-black text-gray-900">{pilier.asIs}/5</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg">
                              <p className="text-sm text-blue-600 font-semibold mb-2">KPI Principal</p>
                              <p className="text-sm font-bold text-gray-900">{kpi.principal}</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
                              <p className="text-sm text-purple-600 font-semibold mb-2">KPI Secondaire</p>
                              <p className="text-xl font-bold text-gray-900">{kpi.secondaire}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
                              <p className="text-sm text-green-600 font-semibold mb-2">Tendance</p>
                              <p className="text-xl font-bold text-gray-900">{kpi.tendance}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ROADMAP */}
                {activeTab === 'roadmap' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <Calendar className="w-8 h-8 text-blue-600" />
                        Roadmap Trimestrielle 2024-2025
                      </h2>
                      <div className="space-y-2">
                        {roadmap.map((trim, i) => (
                          <motion.div
                            key={trim.trimestre}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className={`p-4 rounded-xl ${
                              i === 0 ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300' :
                              'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-4">
                                <div className={`${
                                  i === 0 ? 'bg-blue-600' : 'bg-gray-300'
                                } w-12 h-12 rounded-xl flex items-center justify-center`}>
                                  <span className="text-white font-black text-lg">{trim.trimestre.split(' ')[0]}</span>
                                </div>
                                <div>
                                  <h3 className="text-sm font-bold text-gray-900">{trim.trimestre}</h3>
                                  {i === 0 && <p className="text-blue-600 font-semibold">En cours</p>}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">Budget</p>
                                <p className="text-base font-black text-gray-900">{trim.budget}</p>
                                <p className="text-sm text-gray-600 mt-1">{trim.tactiquesActives} tactiques actives</p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {trim.priorites.map((priorite, j) => (
                                <div key={j} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                                  <CheckCircle className={`w-5 h-5 ${i === 0 ? 'text-blue-600' : 'text-gray-400'}`} />
                                  <span className="text-gray-700 font-medium">{priorite}</span>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* GOUVERNANCE */}
                {activeTab === 'gouvernance' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                      <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                        <Award className="w-8 h-8 text-blue-600" />
                        Gouvernance & Méthodologie BZ
                      </h2>

                      {/* Comité TI */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl mb-6">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Comité TI Trimestriel</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Fréquence</p>
                            <p className="text-lg font-bold text-gray-900">4 fois / an</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Participants</p>
                            <p className="text-lg font-bold text-gray-900">Direction + BZ</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Prochaine rencontre</p>
                            <p className="text-lg font-bold text-blue-600">15 janvier 2025</p>
                          </div>
                        </div>
                      </div>

                      {/* Normes */}
                      <div className="grid grid-cols-2 gap-6 mb-6">
                        <div className="bg-white p-6 rounded-xl border border-gray-200">
                          <Shield className="w-8 h-8 text-blue-600 mb-3" />
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Normes appliquées</h3>
                          <ul className="space-y-2">
                            {['ITIL 4 (100%)', 'NIST 2.0 Cybersecurity Framework', 'ISO 27001:2022', 'Loi 25 (Protection données)'].map((norme, i) => (
                              <li key={i} className="flex items-center gap-2 text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium">{norme}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-white p-6 rounded-xl border border-gray-200">
                          <FileText className="w-8 h-8 text-purple-600 mb-3" />
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Livrables BZ</h3>
                          <ul className="space-y-2">
                            {['Cahiers de charge structurés', 'Livre de minutes PRIsME', 'Rapports KPI trimestriels', 'Roadmap annuelle validée'].map((livrable, i) => (
                              <li key={i} className="flex items-center gap-2 text-gray-700">
                                <CheckCircle className="w-4 h-4 text-purple-600" />
                                <span className="text-sm font-medium">{livrable}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* RACI Global */}
                      <div className="bg-white p-6 rounded-xl border-2 border-blue-200">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Matrice RACI - Responsabilités</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="bg-blue-50 p-4 rounded-xl text-center">
                            <p className="text-3xl font-black text-blue-600 mb-2">R</p>
                            <p className="text-sm font-bold text-gray-700">Responsible</p>
                            <p className="text-xs text-gray-600 mt-2">Qui exécute</p>
                            <p className="text-sm font-semibold text-blue-700 mt-2">Directeur TI BZ</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-xl text-center">
                            <p className="text-3xl font-black text-green-600 mb-2">A</p>
                            <p className="text-sm font-bold text-gray-700">Accountable</p>
                            <p className="text-xs text-gray-600 mt-2">Qui est imputable</p>
                            <p className="text-sm font-semibold text-green-700 mt-2">Direction client</p>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-xl text-center">
                            <p className="text-3xl font-black text-purple-600 mb-2">C</p>
                            <p className="text-sm font-bold text-gray-700">Consulted</p>
                            <p className="text-xs text-gray-600 mt-2">Qui est consulté</p>
                            <p className="text-sm font-semibold text-purple-700 mt-2">Parties prenantes</p>
                          </div>
                          <div className="bg-orange-50 p-4 rounded-xl text-center">
                            <p className="text-3xl font-black text-orange-600 mb-2">I</p>
                            <p className="text-sm font-bold text-gray-700">Informed</p>
                            <p className="text-xs text-gray-600 mt-2">Qui est informé</p>
                            <p className="text-sm font-semibold text-orange-700 mt-2">Équipes / Employés</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* SOLUTIONS D'AFFAIRES */}
                {activeTab === 'solutions' && (
                  <div className="space-y-8">
                    {/* Header avec message d'intégration */}
                    <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-indigo-200">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 p-4 rounded-xl">
                          <Briefcase className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-3xl font-black text-gray-900 mb-2">Solutions d'Affaires</h2>
                          <p className="text-lg text-gray-700 mb-3">
                            Le service de Solutions d'Affaires de BZ accompagne les organisations dans la planification, la structuration et l'optimisation de leurs technologies
                          </p>
                          <div className="flex items-center gap-2 text-sm text-indigo-700 bg-white/60 px-4 py-2 rounded-lg">
                            <Link2 className="w-4 h-4" />
                            <span className="font-semibold">Intégré au Cadre Référentiel BZ • 7 Piliers • 65 Tactiques</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* PRÉSENTATION DU DÉPARTEMENT */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <Target className="w-7 h-7 text-indigo-600" />
                        Présentation du département
                      </h3>
                      <div className="prose max-w-none text-gray-700 leading-relaxed space-y-2 text-sm">
                        <p>
                          Le service de Solutions d'Affaires de BZ accompagne les organisations dans la planification, la structuration et l'optimisation de leurs technologies afin qu'elles deviennent des leviers de performance et de croissance. Notre approche ne se limite pas à implanter des technologies ; elle vise à transformer la manière dont votre organisation exploite le numérique pour créer de la valeur durable.
                        </p>
                        <p>
                          Notre service accompagne chaque client dans la maturité numérique de son organisation, en plaçant la technologie au service de la stratégie, et non l'inverse. Nous agissons comme un partenaire de confiance, capable de traduire les objectifs d'affaires en leviers concrets de performance, de sécurité et de croissance.
                        </p>
                        <p>
                          Découvrez tous les atouts de notre solution : Les bénéfices, une approche intégrée, des étapes de lancement claires et des parcours personnalisés pour répondre parfaitement à vos besoins.
                        </p>
                      </div>
                    </div>

                    {/* LES BÉNÉFICES */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <TrendingUp className="w-7 h-7 text-green-600" />
                        Les bénéfices
                      </h3>
                      <div className="prose max-w-none text-gray-700 leading-relaxed space-y-2 text-sm">
                        <p>
                          Grâce à un accompagnement stratégique et tactique, nous permettons aux entreprises, aux dirigeants et à leurs équipes de :
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          <li>Réduire les risques d'affaires en assurant une gouvernance claire, une conformité réglementaire rigoureuse et une meilleure protection des actifs numériques.</li>
                          <li>Accroître la productivité en optimisant les processus internes, en intégrant des outils performants et en automatisant les tâches à faible valeur ajoutée.</li>
                          <li>Améliorer la performance financière par des gains d'efficacité, une meilleure utilisation des ressources et des décisions appuyées sur des données fiables et en temps réel.</li>
                          <li>Stimuler l'innovation en favorisant l'adoption de l'intelligence artificielle, de l'automatisation et des nouvelles technologies dans les opérations quotidiennes.</li>
                          <li>Renforcer la collaboration et la satisfaction interne grâce à des environnements numériques modernes et unifiés qui simplifient la communication et l'accès à l'information.</li>
                          <li>Rehausser l'expérience client en offrant des interactions plus rapides, plus personnalisées et plus cohérentes à chaque point de contact.</li>
                          <li>Assurer une transformation numérique durable, alignée sur les priorités d'affaires, la culture interne et la capacité d'évolution de l'organisation.</li>
                        </ul>
                      </div>
                    </div>

                    {/* NOTRE DÉMARCHE */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <CheckCircle className="w-7 h-7 text-blue-600" />
                        Notre démarche
                      </h3>
                      <div className="prose max-w-none text-gray-700 leading-relaxed space-y-2 text-sm">
                        <p>
                          En misant sur la synergie entre notre service-conseil, nos experts TI et nos partenaires externes vigoureusement sélectionnés, votre organisation bénéficie d'une démarche optimale :
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          <li>Analyse exhaustive de l'environnement, des besoins, des objectifs et des capacités ;</li>
                          <li>Recommandations de solutions efficientes alignées sur la stratégie d'affaires ;</li>
                          <li>Implantation rigoureuse des mesures requises ;</li>
                          <li>Gestion proactive des opérations TI avec veille technologique continue ;</li>
                          <li>Optimisation et mise à jour régulières de la stratégie pour assurer la performance et l'adaptabilité de vos processus numériques</li>
                        </ul>
                      </div>
                    </div>

                    {/* NOTRE APPROCHE INTÉGRÉE */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <Database className="w-7 h-7 text-indigo-600" />
                        Notre approche intégrée
                      </h3>
                      <div className="prose max-w-none text-gray-700 leading-relaxed space-y-2 mb-3 text-sm">
                        <p>
                          Notre approche repose sur une compréhension stratégique des besoins d'affaires, combinée à une expertise technologique approfondie et à la collaboration d'experts internes provenant de différents secteurs et de nos partenaires externes sélectionnés.
                        </p>
                        <p>
                          En travaillant main dans la main, nous offrons une vision cohérente, qui prend en compte à la fois les enjeux humains, technologiques et organisationnels (processus).
                        </p>
                        <p>
                          Nos interventions se déploient à travers six grands champs d'intervention complémentaires, couvrant l'ensemble du cycle de maturité numérique d'une organisation. Chaque volet poursuit un objectif clair et génère des avantages concrets, tant sur le plan opérationnel que stratégique.
                        </p>
                      </div>

                      {/* 6 CHAMPS D'INTERVENTION */}
                      <div className="space-y-6 mt-8">
                        {/* Champ 1: Gouvernance et conformité */}
                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="bg-gradient-to-br from-purple-500 to-indigo-500 p-3 rounded-xl">
                              <Award className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-xl font-black text-gray-900">Gouvernance et conformité</h4>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 4: Gouvernance & Organisation"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 4
                                  </button>
                                  <button
                                    onClick={() => setActiveTab('tactiques')}
                                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir les tactiques: Comité TI, RACI, Politiques"
                                  >
                                    <Target className="w-3 h-3" />
                                    Tactiques
                                  </button>
                                </div>
                              </div>
                              <p className="text-gray-700 mt-1 text-sm">
                                Mettre en place une gestion TI claire, structurée et conforme aux meilleures pratiques pour soutenir les décisions stratégiques.
                              </p>
                              <p className="text-gray-600 mt-2 text-sm">
                                <strong>Bénéfices:</strong> Réduction des risques d'affaires, conformité à la Loi 25 et aux cadres normatifs (ISO, NIST), amélioration de la crédibilité et de la confiance des partenaires.
                              </p>
                              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-purple-200">
                                <p className="text-xs font-semibold text-purple-900 mb-1">🔗 Connexions Cadre BZ:</p>
                                <p className="text-xs text-gray-700">
                                  <strong>Pilier 4 - Gouvernance & Organisation:</strong> Comité TI structurant (P4-T01), Matrice RACI (P4-T02), Politiques TI (P4-T03). 
                                  <strong>Pilier 3 - Sécurité & Conformité:</strong> Conformité Loi 25 (P3-T08), Cadres normatifs ISO/NIST (P3-T09).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Champ 2: Infrastructures et cybersécurité */}
                        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="bg-gradient-to-br from-red-500 to-orange-500 p-3 rounded-xl">
                              <Shield className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-xl font-black text-gray-900">Infrastructures et cybersécurité</h4>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 2: Architecture & Infrastructure"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 2
                                  </button>
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 3: Sécurité & Conformité"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 3
                                  </button>
                                </div>
                              </div>
                              <p className="text-gray-700 mt-1 text-sm">
                                Assurer la sécurité et maximiser la disponibilité continue des environnements technologiques.
                              </p>
                              <p className="text-gray-600 mt-2 text-sm">
                                <strong>Bénéfices:</strong> Protection renforcée des données, continuité des opérations en cas d'incident, diminution des vulnérabilités et hausse de la résilience organisationnelle.
                              </p>
                              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-red-200">
                                <p className="text-xs font-semibold text-red-900 mb-1">🔗 Connexions Cadre BZ:</p>
                                <p className="text-xs text-gray-700">
                                  <strong>Pilier 2 - Architecture & Infrastructure:</strong> Inventaire TI (P2-T01), Architecture TI (P2-T05), Plan continuité (P2-T08). 
                                  <strong>Pilier 3 - Sécurité & Conformité:</strong> Politique sécurité (P3-T01), Audit sécurité (P3-T02), Gestion vulnérabilités (P3-T03), Plan reprise (P3-T06).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Champ 3: Applications et solutions métiers */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-3 rounded-xl">
                              <Database className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-xl font-black text-gray-900">Applications et solutions métiers</h4>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 1: Besoins & Alignement"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 1
                                  </button>
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 2: Architecture & Infrastructure"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 2
                                  </button>
                                </div>
                              </div>
                              <p className="text-gray-700 mt-1 text-sm">
                                Moderniser et intégrer les outils numériques pour soutenir la performance et la collaboration.
                              </p>
                              <p className="text-gray-600 mt-2 text-sm">
                                <strong>Bénéfices:</strong> Amélioration de l'efficacité opérationnelle, centralisation des données, automatisation des processus et meilleure prise de décision.
                              </p>
                              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-blue-200">
                                <p className="text-xs font-semibold text-blue-900 mb-1">🔗 Connexions Cadre BZ:</p>
                                <p className="text-xs text-gray-700">
                                  <strong>Pilier 1 - Besoins & Alignement:</strong> Analyse besoins métiers (P1-T01), Alignement stratégique (P1-T02), Roadmap TI (P1-T05). 
                                  <strong>Pilier 2 - Architecture & Infrastructure:</strong> Architecture TI (P2-T05), Intégration applications (P2-T06), Catalogue services (P2-T07).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Champ 4: Processus et amélioration */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-3 rounded-xl">
                              <CheckCircle className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-xl font-black text-gray-900">Processus et amélioration</h4>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 4: Gouvernance & Organisation"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 4
                                  </button>
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 5: Opérations & Support"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 5
                                  </button>
                                </div>
                              </div>
                              <p className="text-gray-700 mt-1 text-sm">
                                Optimiser des méthodes de travail pour accroître la productivité et éliminer les inefficacités internes.
                              </p>
                              <p className="text-gray-600 mt-2 text-sm">
                                <strong>Bénéfices:</strong> Réduction des coûts, gain de temps, normalisation des pratiques, développement des compétences et adoption d'une culture d'amélioration continue.
                              </p>
                              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-green-200">
                                <p className="text-xs font-semibold text-green-900 mb-1">🔗 Connexions Cadre BZ:</p>
                                <p className="text-xs text-gray-700">
                                  <strong>Pilier 4 - Gouvernance & Organisation:</strong> Processus ITIL 4 (P4-T04), Amélioration continue (P4-T05). 
                                  <strong>Pilier 4 - Gestion des Services TI:</strong> Service Desk (P4-T01), Base connaissances (P4-T02), Gestion incidents (P4-T01), Gestion changements (P4-T05).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Champ 5: Intelligence artificielle et automatisation */}
                        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="bg-gradient-to-br from-yellow-500 to-orange-500 p-3 rounded-xl">
                              <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-xl font-black text-gray-900">Intelligence artificielle et automatisation</h4>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 6: Innovation & IA"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 6
                                  </button>
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 7: IA & Programmation"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 7
                                  </button>
                                </div>
                              </div>
                              <p className="text-gray-700 mt-1 text-sm">
                                Utiliser l'IA et les données comme leviers stratégiques pour améliorer la performance et soutenir la prise de décision.
                              </p>
                              <p className="text-gray-600 mt-2 text-sm">
                                <strong>Bénéfices:</strong> Automatisation des tâches répétitives, analyses prédictives, décisions plus rapides et concentration des équipes sur des activités à forte valeur ajoutée.
                              </p>
                              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-yellow-200">
                                <p className="text-xs font-semibold text-yellow-900 mb-1">🔗 Connexions Cadre BZ:</p>
                                <p className="text-xs text-gray-700">
                                  <strong>Pilier 6 - Innovation & Amélioration continue:</strong> Automatisation processus (P6-T02), Ateliers innovation (P6-T03). 
                                  <strong>Pilier 7 - Intelligence Artificielle & Programmation:</strong> Stratégie IA (P7-T01), Cas usage IA (P7-T02), Veille IA (P7-T03), Intégration IA (P7-T04).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Champ 6: Expérience numérique et transformation client */}
                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="bg-gradient-to-br from-indigo-500 to-purple-500 p-3 rounded-xl">
                              <Headphones className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-xl font-black text-gray-900">Expérience numérique et transformation client</h4>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 5: Opérations & Support"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 5
                                  </button>
                                  <button
                                    onClick={() => setActiveTab('piliers')}
                                    className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                    title="Voir le Pilier 7: Communication & Formation"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    Pilier 7
                                  </button>
                                </div>
                              </div>
                              <p className="text-gray-700 mt-1 text-sm">
                                Créer des expériences numériques fluides et cohérentes pour les employés et les clients.
                              </p>
                              <p className="text-gray-600 mt-2 text-sm">
                                <strong>Bénéfices:</strong> Hausse de la satisfaction et de la fidélisation, amélioration de la collaboration, valorisation de la marque employeur et modernisation de l'image organisationnelle.
                              </p>
                              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-indigo-200">
                                <p className="text-xs font-semibold text-indigo-900 mb-1">🔗 Connexions Cadre BZ:</p>
                                <p className="text-xs text-gray-700">
                                  <strong>Pilier 4 - Gestion des Services TI:</strong> Service Desk (P4-T01), Satisfaction client (P4-T04), Portail services (P4-T10). 
                                  <strong>Pilier 7 - Communication & Formation:</strong> Formation utilisateurs (P7-T01), Communication TI (P7-T02), Documentation (P7-T03).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* PHASE INITIALE : LA RENCONTRE EXPLORATOIRE */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <Users className="w-7 h-7 text-indigo-600" />
                        Phase initiale : La rencontre exploratoire
                      </h3>
                      <div className="prose max-w-none text-gray-700 leading-relaxed space-y-2 mb-3 text-sm">
                        <p>
                          Dans le cadre de notre démarche, nous souhaitons aborder plusieurs thématiques essentielles avec vous et les membres de votre équipe qui jouent un rôle clé dans la gestion de l'information. Cette approche vise à mieux comprendre votre organisation et vos besoins stratégiques, afin de définir ensemble le parcours le plus approprié. En réunissant les parties prenantes concernées, nous serons en mesure de cerner précisément vos enjeux et de cibler les axes d'amélioration prioritaires. Cette collaboration permettra de bâtir une vision commune et d'assurer que les solutions proposées soient en adéquation avec vos objectifs.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Objectifs organisationnels</h4>
                            <button
                              onClick={() => setActiveTab('piliers')}
                              className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir le Pilier 1: Besoins & Alignement"
                            >
                              <Link2 className="w-3 h-3" />
                              P1
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Nous discuterons de la mission de votre organisation et des objectifs stratégiques auxquels vous aspirez pour les prochaines années. Nous aborderons également vos priorités d'affaires à court terme et les défis organisationnels que vous rencontrez, tout en évaluant le rôle de la technologie dans l'atteinte de vos ambitions.
                          </p>
                          <p className="text-xs text-blue-700 mt-2 italic">🔗 Connecté au <strong>Pilier 1 - Besoins & Alignement TI</strong> (P1-T01: Analyse besoins métiers, P1-T02: Alignement stratégique)</p>
                        </div>

                        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Contexte d'affaires</h4>
                            <button
                              onClick={() => setActiveTab('piliers')}
                              className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir le Pilier 1: Besoins & Alignement"
                            >
                              <Link2 className="w-3 h-3" />
                              P1
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Cette section vise à brosser un portrait global de votre structure organisationnelle, de votre secteur d'activité et de la répartition des équipes. Nous explorerons également la configuration de votre équipe TI et les modalités de travail adoptées au sein de votre entreprise.
                          </p>
                          <p className="text-xs text-green-700 mt-2 italic">🔗 Connecté au <strong>Pilier 1 - Besoins & Alignement TI</strong> (P1-T01: Analyse besoins métiers, compréhension contexte organisationnel)</p>
                        </div>

                        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Infrastructure TI</h4>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 2: Architecture & Infrastructure"
                              >
                                <Link2 className="w-3 h-3" />
                                P2
                              </button>
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 2: Catalogue des actifs"
                              >
                                <Link2 className="w-3 h-3" />
                                P2
                              </button>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Nous réaliserons un portrait de l'état actuel de votre infrastructure technologique, que ce soit sur site, en infonuagique ou de façon hybride. Seront également abordés les logiciels utilisés pour vos opérations, votre niveau de satisfaction envers ces outils, les projets technologiques en cours ou prévus, ainsi que les principaux irritants ou enjeux rencontrés.
                          </p>
                          <p className="text-xs text-purple-700 mt-2 italic">🔗 Connecté au <strong>Pilier 2 - Architecture & Infrastructure</strong> (P2-T01: Inventaire TI, P2-T05: Architecture TI)</p>
                        </div>

                        <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Posture en cybersécurité</h4>
                            <button
                              onClick={() => setActiveTab('piliers')}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir le Pilier 3: Sécurité & Conformité"
                            >
                              <Link2 className="w-3 h-3" />
                              P3
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Cette partie permettra d'aborder les mesures de sécurité en place, dont les politiques, outils et solutions de sauvegarde des données. Nous examinerons les expériences d'incidents passés, la formation offerte aux membres du personnel, ainsi que la conformité à la Loi 25 sur la protection des données personnelles.
                          </p>
                          <p className="text-xs text-red-700 mt-2 italic">🔗 Connecté au <strong>Pilier 5 - Gouvernance Cybersécurité</strong> (P5-T01: Gouvernance, P5-T02: Identifier, P5-T03: Protéger selon NIST CSF 2.0)</p>
                        </div>

                        <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Utilisation de l'intelligence artificielle (IA)</h4>
                            <button
                              onClick={() => setActiveTab('piliers')}
                              className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir le Pilier 7: IA & Programmation"
                            >
                              <Link2 className="w-3 h-3" />
                              P7
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Nous ferons le point sur l'intégration actuelle de l'IA dans vos processus d'affaires, les outils utilisés, et les opportunités futures pour son adoption. Les préoccupations et freins liés à l'IA seront discutés, tout comme les départements où cette technologie pourrait maximiser la création de valeur.
                          </p>
                          <p className="text-xs text-yellow-700 mt-2 italic">🔗 Connecté au <strong>Pilier 7 - Intelligence Artificielle & Programmation</strong> (P7-T01: Stratégie IA, P7-T02: Cas usage IA, P7-T04: Intégration IA)</p>
                        </div>

                        <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Besoins et opportunités</h4>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 1: Besoins & Alignement"
                              >
                                <Link2 className="w-3 h-3" />
                                P1
                              </button>
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 4: Gouvernance & Organisation"
                              >
                                <Link2 className="w-3 h-3" />
                                P4
                              </button>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Enfin, nous explorerons les possibilités d'externalisation de fonctions TI et le type de soutien dont vous pourriez bénéficier (infogérance, cybersécurité, gestion de projet, conseils stratégiques). Nous conclurons avec un survol des aspects technologiques ou organisationnels pour lesquels un accompagnement pourrait vous être utile.
                          </p>
                          <p className="text-xs text-indigo-700 mt-2 italic">🔗 Connecté aux <strong>Piliers 1 & 4</strong> - Identification besoins (P1-T01) et opportunités d'accompagnement stratégique (P4-T01: Comité TI)</p>
                        </div>
                      </div>
                    </div>

                    {/* LES POSSIBILITÉS DE PARCOURS D'AFFAIRES */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <Calendar className="w-7 h-7 text-indigo-600" />
                        Les possibilités de parcours d'affaires
                      </h3>

                      {/* Tableau des parcours */}
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full border-collapse bg-white rounded-lg shadow-md">
                          <thead>
                            <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                              <th className="border border-gray-300 px-4 py-3 text-left font-bold">Étapes</th>
                              <th className="border border-gray-300 px-4 py-3 text-center font-bold">A</th>
                              <th className="border border-gray-300 px-4 py-3 text-center font-bold">B</th>
                              <th className="border border-gray-300 px-4 py-3 text-center font-bold">C</th>
                              <th className="border border-gray-300 px-4 py-3 text-center font-bold">D</th>
                              <th className="border border-gray-300 px-4 py-3 text-center font-bold">E</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 font-medium">Rencontre exploratoire</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                            </tr>
                            <tr className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 font-medium">Évaluations préliminaire globale</td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                            </tr>
                            <tr className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 font-medium">Établissement d'un comité TI (Structurant)</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                            </tr>
                            <tr className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 font-medium">Audit de sécurité (Cybersécurité)</td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                            </tr>
                            <tr className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 font-medium">Bilan de santé technologique (TI)</td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center"></td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                            </tr>
                            <tr className="hover:bg-gray-50 bg-blue-50">
                              <td className="border border-gray-300 px-4 py-3 font-bold">Élaboration d'un plan d'action (Spécifications d'exigences)</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                              <td className="border border-gray-300 px-4 py-3 text-center">✔️</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Descriptions des étapes */}
                      <div className="space-y-2">
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Rencontre exploratoire</h4>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 1: Besoins & Alignement"
                              >
                                <Link2 className="w-3 h-3" />
                                P1
                              </button>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Une rencontre exploratoire est essentielle pour comprendre vos objectifs organisationnels, votre contexte d'affaires, votre infrastructure TI, votre posture en cybersécurité et votre utilisation de l'intelligence artificielle.
                          </p>
                          <p className="text-xs text-blue-700 mt-2 italic">🔗 Connecté au <strong>Pilier 1 - Besoins & Alignement TI</strong> - Phase initiale de l'analyse holistique (P1-T01)</p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Évaluation préliminaire globale</h4>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir tous les Piliers BZ"
                              >
                                <Link2 className="w-3 h-3" />
                                Tous
                              </button>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Remise d'un plan directeur qui vise à fournir une évaluation complète de l'organisation sur trois volets essentiels : Technologies de l'information (TI), Intelligence Artificielle (IA) et Cybersécurité. Cette évaluation repose sur les trois axes suivants : processus, humain et technologies. Recommandation constat et prochaine étape.
                          </p>
                          <p className="text-xs text-indigo-700 mt-2 italic">🔗 Connecté à <strong>tous les 7 Piliers BZ</strong> - Évaluation holistique couvrant l'ensemble du Cadre Référentiel (processus, humain, technologies)</p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Établissement d'un comité TI</h4>
                            <button
                              onClick={() => setActiveTab('piliers')}
                              className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir le Pilier 4: Gouvernance & Organisation - Tactique P4-T01"
                            >
                              <Link2 className="w-3 h-3" />
                              P4-T01
                            </button>
                          </div>
                          <ul className="text-gray-700 text-sm space-y-1 ml-4 list-disc">
                            <li>Suivi Périodique</li>
                            <li>Approche stratégique, tactique et/ou opérationnel</li>
                          </ul>
                          <p className="text-xs text-purple-700 mt-2 italic">🔗 Connecté à la <strong>Tactique P4-T01 - Comité TI</strong> du Pilier 4 (Gouvernance & Organisation)</p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Audit de sécurité (Cybersécurité)</h4>
                            <button
                              onClick={() => setActiveTab('piliers')}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir le Pilier 5: Gouvernance Cybersécurité - Tactique P5-T02 (Identifier)"
                            >
                              <Link2 className="w-3 h-3" />
                              P5-T02
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Un audit de sécurité préliminaire constitue une évaluation structurée de votre posture en cybersécurité. Il combine une étude sommaire de vos processus, un recensement de vos actifs TI critiques, la détection de vulnérabilités, la simulation de menaces et l'interprétation des résultats. Son objectif est de vous fournir un portrait clair et concret des risques à corriger pour renforcer votre sécurité.
                          </p>
                          <p className="text-xs text-red-700 mt-2 italic">🔗 Connecté à la <strong>Tactique P5-T02 - Identifier</strong> du Pilier 5 (Gouvernance Cybersécurité - NIST CSF 2.0)</p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Bilan de santé technologique (TI)</h4>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 1: Besoins & Alignement"
                              >
                                <Link2 className="w-3 h-3" />
                                P1
                              </button>
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 2: Architecture & Infrastructure"
                              >
                                <Link2 className="w-3 h-3" />
                                P2
                              </button>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Évaluation exhaustive de l'état, de la performance et de la sécurité de votre parc informatique. Ce processus permet d'identifier les problèmes potentiels, les inefficacités, les goulots d'étranglements et les domaines d'amélioration de votre environnement. Suite à cette analyse, les conclusions, les recommandations et les actions nécessaires pour garantir la fiabilité, la sécurité et la performance de votre parc informatique vous seront communiquées.
                          </p>
                          <p className="text-xs text-blue-700 mt-2 italic">🔗 Connecté aux <strong>Piliers 1 & 2</strong> - Évaluation holistique couvrant Besoins & Alignement (P1) et Architecture & Infrastructure (P2)</p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">Élaboration d'un plan d'action (Spécifications d'exigences)</h4>
                            <button
                              onClick={() => setActiveTab('roadmap')}
                              className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                              title="Voir la section Roadmap du Cadre BZ"
                            >
                              <Link2 className="w-3 h-3" />
                              Roadmap
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm">
                            Un audit de sécurité préliminaire constitue une évaluation structurée de votre posture en cybersécurité. Il combine une étude sommaire de vos processus, un recensement de vos actifs TI critiques, la détection de vulnérabilités, la simulation de menaces et l'interprétation des résultats. Son objectif est de vous fournir un portrait clair et concret des risques à corriger pour renforcer votre sécurité. L'élaboration d'un plan d'action (Spécifications d'exigences) consiste à définir de manière structurée et détaillée les étapes clés, les responsabilités, les ressources nécessaires et les échéanciers pour réaliser les recommandations (12 mois).
                          </p>
                          <p className="text-gray-700 text-sm mt-2">
                            Votre directeur de compte et votre directeur solutions d'affaires suivra la mise en place des étapes jusqu'à la présentation des Spécifications d'exigences et des recommandations.
                          </p>
                          <p className="text-xs text-indigo-700 mt-2 italic">🔗 Connecté à la <strong>section Roadmap</strong> du Cadre BZ - Planification stratégique 12-24 mois avec tactiques priorisées</p>
                        </div>
                      </div>
                    </div>

                    {/* LES SERVICES OFFERTS */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <FileText className="w-7 h-7 text-indigo-600" />
                        Les services offerts
                      </h3>
                      <div className="prose max-w-none text-gray-700 leading-relaxed space-y-2 mb-3 text-sm">
                        <p>
                          Nos services sont conçus pour vous accompagner autant dans la planification stratégique annuelle que pour des interventions ponctuelles. Qu'il s'agisse d'audits de cybersécurité, de bilans technologiques ou d'élaboration de plans d'action personnalisés, notre équipe adapte son soutien selon vos besoins spécifiques et votre contexte.
                        </p>
                      </div>

                      {/* Tableau des services - version simplifiée pour l'affichage */}
                      <div className="overflow-x-auto">
                        <div className="space-y-2">
                          {/* Gestion de projets */}
                          <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-lg">Gestion de projets</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Infrastructure:</strong> Arborescence SharePoint, Migrations TI, Configuration de sécurité (antivirus, 2FA, etc.), Installation de matériel TI</p>
                              <p><strong>Gestion des opérations:</strong> Implantation et gestion de projets TI, Élaboration du plan directeur TI, Élaboration du calendrier annuel d'activités, Gestion et suivi de projets multi-départements</p>
                            </div>
                          </div>

                          {/* Ressources humaines et accompagnement */}
                          <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <h4 className="font-bold text-gray-900 text-lg">Ressources humaines et accompagnement</h4>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => setActiveTab('piliers')}
                                  className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                  title="Voir le Pilier 4: Gouvernance & Organisation"
                                >
                                  <Link2 className="w-3 h-3" />
                                  P4
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Développement des compétences:</strong> Accompagnement & conseil, Élaboration de votre plan de formations, Parcours de formation Talamus, Séances éducatives (« Introduction à la cybersécurité », « Évitez d'être hameçonnés », « Démystifier la Loi 25 », « Introduction à Copilot », etc.), Questionnaire et rapport post-séance éducative</p>
                              <p><strong>Accompagnement stratégique:</strong> Comité TI (opérationnel, tactique et stratégique), Accompagnement des demandes d'assureurs (formulaires techniques)</p>
                            </div>
                            <p className="text-xs text-purple-700 mt-3 italic">🔗 Connecté au <strong>Pilier 4 - Gouvernance & Organisation</strong> (P4-T01: Comité TI structurant avec approche stratégique/tactique/opérationnelle)</p>
                          </div>

                          {/* Cybersécurité */}
                          <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <h4 className="font-bold text-gray-900 text-lg">Cybersécurité</h4>
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 5: Gouvernance Cybersécurité (NIST CSF 2.0)"
                              >
                                <Link2 className="w-3 h-3" />
                                P5
                              </button>
                            </div>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Gouvernance et politique:</strong> Politique de cybersécurité (cadre d'utilisation), Politique et plan de sauvegarde, Plan d'intervention en cas d'incident de cybersécurité, Plan de reprise des activités (PRA), Plan de restauration</p>
                              <p><strong>Évaluation et détection:</strong> Campagnes d'hameçonnage personnalisées ou continues, Surveillance du Dark web, Balayages de vulnérabilités (interne / externe), Audit de sécurité, Rapport d'évaluation des risques, Rapport de cote de sécurité M365, Bilan de santé technologique</p>
                              <p><strong>Conformité et continuité:</strong> Programme santé et sécurité technologique (PSST), Campagnes d'hameçonnage automatisées, Accès au portail documentaire Loi 25, Accompagnement Loi 25 et mises à jour annuelles, Évaluation des facteurs relatifs à la vie privée (ÉFVP), Third Party Risk Assessment</p>
                            </div>
                            <p className="text-xs text-red-700 mt-3 italic">🔗 Connecté au <strong>Pilier 5 - Gouvernance Cybersécurité</strong> selon NIST CSF 2.0 (P5-T01: Gouvernance, P5-T02: Identifier, P5-T03: Protéger, P5-T04: Détecter, P5-T05: Répondre, P5-T06: Récupérer)</p>
                          </div>

                          {/* Intelligence artificielle (IA) */}
                          <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <h4 className="font-bold text-gray-900 text-lg">Intelligence artificielle (IA)</h4>
                              <button
                                onClick={() => setActiveTab('piliers')}
                                className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded-full transition-colors flex items-center gap-1"
                                title="Voir le Pilier 7: IA & Programmation"
                              >
                                <Link2 className="w-3 h-3" />
                                P7
                              </button>
                            </div>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Accompagnement et intégration:</strong> Conseil stratégique IA, Intégration de solutions IA, Politique d'utilisation de l'IA, Formation à Copilot et IA, Mise en relation avec partenaires IA</p>
                            </div>
                            <p className="text-xs text-yellow-700 mt-3 italic">🔗 Connecté au <strong>Pilier 7 - Intelligence Artificielle & Programmation</strong> (P7-T01: Stratégie IA, P7-T02: Cas usage IA, P7-T04: Intégration IA, P7-T05: Politique IA)</p>
                          </div>

                          {/* Lois et réglementations */}
                          <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-lg">Lois et réglementations</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Conformité légale:</strong> Conformité Loi 25 (préparation, accompagnement, mise à jour), Accompagnement des demandes d'assureurs, Accompagnement aux certifications SOC, ISO, SIEM, PCI DSS, etc.</p>
                            </div>
                          </div>

                          {/* Télécommunications */}
                          <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-lg">Télécommunications</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Communications unifiées:</strong> Conseil et accompagnement en téléphonie et connectivité, Optimisation des coûts et performance réseau</p>
                            </div>
                          </div>

                          {/* Programmation et développement */}
                          <div className="bg-pink-50 rounded-xl p-5 border border-pink-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-lg">Programmation et développement</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Développement applicatif:</strong> Projets de programmation sur mesure, Automatisation de processus métiers, Intégration d'applications (API, Power Platform, etc.)</p>
                            </div>
                          </div>

                          {/* Analyse */}
                          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-lg">Analyse</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p><strong>Actifs et ressources:</strong> Établissement du catalogue des actifs, Suivi des licences et inventaires logiciels</p>
                              <p><strong>Tests et validation:</strong> Test de restauration partiel, Test de restauration complet</p>
                              <p><strong>Audits et bilans:</strong> Audit de sécurité préliminaire, Bilan de santé technologique général, Évaluations préliminaire globale</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* LEXIQUE */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                      <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                        <BookOpen className="w-7 h-7 text-indigo-600" />
                        Lexique
                      </h3>
                      <div className="space-y-2">
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Environnement Mon BZ</h4>
                          <p className="text-gray-700 text-sm">
                            Notre portail client offre une interface complète pour la consultation et la gestion de toutes vos opérations, passées et en cours. Cela inclut la possibilité d'accéder à vos billets de service, de suivre l'avancement de vos projets avec leurs différentes phases et tâches, de vérifier l'état de vos sauvegardes, ainsi que d'explorer toutes les données pertinentes concernant votre parc informatique
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Plan directeur</h4>
                          <p className="text-gray-700 text-sm">
                            Votre plan directeur offrira une vision claire de l'évolution de vos besoins et vous permettra d'anticiper les tendances technologiques et les opportunités émergentes. Il inclura également, l'élaboration d'un budget pour vos dépenses TI, assurant ainsi une gestion financière responsable et une allocation judicieuse de vos ressources pour soutenir votre croissance et votre succès.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Diagnostic TI de base</h4>
                          <p className="text-gray-700 text-sm">
                            Évaluation sommaire de l'environnement TI permettant de cibler certaines pistes améliorations qui feront l'objet de recommandations.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Bilan de santé technologique</h4>
                          <p className="text-gray-700 text-sm">
                            Évaluation exhaustive de l'état, de la performance et de la sécurité de votre parc informatique. Ce processus permet d'identifier les problèmes potentiels, les inefficacités, les goulots d'étranglements et les domaines d'amélioration de votre environnement. Suite à cette analyse, les conclusions, les recommandations et les actions nécessaires pour garantir la fiabilité, la sécurité et la performance de votre parc informatique vous seront communiquées.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Antivirus EDR</h4>
                          <p className="text-gray-700 text-sm">
                            Contrairement aux antivirus traditionnels, l'EDR offre une protection avancée. Il ne se contente pas de détecter et de bloquer les menaces connues, il surveille continuellement l'activité des points de terminaison. Ainsi, lorsqu'il détecte des comportements suspects, il répond et remédie à ceux-ci.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Désastre</h4>
                          <p className="text-gray-700 text-sm mb-2">
                            Événement majeur qui engendre une perturbation des fonctionnalités de l'environnement, mettant en péril la continuité de l'ensemble des opérations de l'entreprise. Par exemple :
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-700">
                            <li>Une cyber-attaque</li>
                            <li>Une catastrophe naturelle</li>
                            <li>Un incident de vol</li>
                            <li>Contraction d'un virus</li>
                          </ul>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Outil Cyber-BZ</h4>
                          <p className="text-gray-700 text-sm mb-2">
                            Notre portail client offre une suite complète de fonctionnalités, notamment un catalogue des actifs incluant les risques et les responsables reliés à ceux-ci, un tableau de bord pour faciliter la gestion, ainsi que la capacité de générer des rapports. Cet ensemble d'outils permet une approche complète d'identification, de protection, de détection, de réponse et de récupération en cas d'incident et inclut :
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-700">
                            <li>Un aide-mémoire des obligations à réaliser et des éléments tangibles à produire</li>
                            <li>Le Registre des incidents</li>
                            <li>Une Gouvernance sur les rôles et responsabilités (Matrice RACI)</li>
                            <li>Des politiques et pratiques de gouvernance pour la protection des renseignements personnels</li>
                            <li>Une Politique de confidentialité</li>
                            <li>Un plan d'intervention en cas d'incident de confidentialité</li>
                            <li>Un rapport d'incident de confidentialité</li>
                            <li>Des gabarits de demande de consentement en lien avec les renseignements personnels pour les employés et pour les clients</li>
                            <li>Un Inventaire des renseignements personnels</li>
                            <li>Un Inventaire de vos actifs</li>
                          </ul>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Fonction d'identification Cyber-BZ</h4>
                          <p className="text-gray-700 text-sm">
                            Un accompagnement personnalisé afin de bâtir la fonction d'identification (ID) et développer une compréhension exhaustive permettant de gérer les risques cyber des systèmes, des données et actifs de manière plus efficace. Les données, le personnel, les appareils, les systèmes et les installations qui permettent à l'organisation d'atteindre les objectifs métier sont identifiés et gérés conformément avec leur importance relative pour les objectifs organisationnels et la stratégie de risques de l'organisation.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Matrice RACI</h4>
                          <p className="text-gray-700 text-sm">
                            Cet outil vous permettra de clarifier et de définir les rôles et responsabilités des parties prenantes de votre stratégie en cybersécurité. Le « R » correspond au responsable, le « A » à la/les personne(s) qui approuve(nt), le « C » aux personnes consultées lors de la prise d'une décision et le « I » aux personnes informées des décisions.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Service de préparation d'information technique à l'exécution des demandes externes en Cybersécurité</h4>
                          <p className="text-gray-700 text-sm">
                            Le temps et coûts associés à l'exécution des demandes d'audit, d'analyse, d'évaluation, d'examen ou autres services en cybersécurité, provenant du client ou de toute firme externe, feront l'objet d'une facturation distincte de l'entente de service.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Élaboration de rapports d'évaluations des facteurs relatifs à la vie privée (ÉFVP)</h4>
                          <p className="text-gray-700 text-sm">
                            Les évaluations des facteurs relatifs à la vie privée (ÉFVP) et les demandes spécifiques, telles que les rapports demandés par des tiers, sont exclues de l'offre standard et seront facturées séparément si non spécifiées à l'offre de service.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">Service de configuration de l'environnement technologique pour l'IA</h4>
                          <p className="text-gray-700 text-sm">
                            Les temps et coûts associés à l'exécution des demandes de configuration de l'environnement technologique, à la validation de l'environnement TI, à la préparation d'un schéma de l'environnement TI du client ou à toute autre tâche, provenant de toute firme externe ou d'un projet d'intégration, d'amélioration ou d'entretien en intelligence artificielle (IA), feront l'objet d'une facturation distincte de l'entente de service.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">BZ SWAT (incident cybersécurité)</h4>
                          <p className="text-gray-700 text-sm">
                            Les équipes sont prêts à intervenir rapidement dans votre environnement technologique en cas de désastre à la suite d'une attaque en cybersécurité, afin de rétablir la situation dans les meilleurs délais. Ce service optionnel est mis à la disposition des clients pour une durée maximale déterminée dans le contrat. Au-delà de cette durée, des frais seront facturés selon les tarifs en vigueur.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-2">BZ SWAT (autre incident)</h4>
                          <p className="text-gray-700 text-sm">
                            Les équipes sont prêts à intervenir rapidement dans votre environnement technologique en cas de désastre autre qu'une attaque en cybersécurité, afin de rétablir la situation dans les meilleurs délais. Ce service optionnel est mis à la disposition des clients pour une durée maximale déterminée dans le contrat. Au-delà de cette durée, des frais seront facturés selon les tarifs en vigueur.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* SECTION : CE QUE PRIsME APPORTE EN PLUS */}
                    <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 rounded-xl p-4 shadow-2xl border-2 border-indigo-400/50">
                      <div className="text-center mb-4">
                        <div className="inline-block bg-white/20 backdrop-blur-sm p-2 rounded-xl mb-2">
                          <Sparkles className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-black text-white mb-2">
                          Solutions d'Affaires + PRIsME = Excellence Complète
                        </h3>
                        <p className="text-sm text-indigo-100 max-w-3xl mx-auto">
                          Votre approche est solide. PRIsME la structure, l'automatise et la transforme en solution complète et reproductible.
                        </p>
                      </div>

                      {/* Spider Charts Comparatifs - Côte à côte */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        {/* Spider Chart 1: Aspect "Ce qui est touché" */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                          <div className="text-center mb-3">
                            <div className="inline-block bg-yellow-500/30 px-4 py-1.5 rounded-full mb-2">
                              <span className="text-lg font-black text-white">35%</span>
                            </div>
                            <h4 className="text-sm font-bold text-white">Aspect : Ce qui est touché</h4>
                            <p className="text-xs text-indigo-200 mt-1">Couverture fonctionnelle</p>
                          </div>
                          <ResponsiveContainer width="100%" height={280}>
                            <RadarChart data={[
                              { dimension: 'Plateforme\nTechno', 'Solutions d\'Affaires': 2, 'PRIsME': 10 },
                              { dimension: 'Gouvernance', 'Solutions d\'Affaires': 4, 'PRIsME': 10 },
                              { dimension: 'Portails\nClients', 'Solutions d\'Affaires': 1, 'PRIsME': 10 },
                              { dimension: 'Documents\nITIL/COBIT', 'Solutions d\'Affaires': 3, 'PRIsME': 10 },
                              { dimension: 'Méthodologie', 'Solutions d\'Affaires': 5, 'PRIsME': 10 },
                              { dimension: 'Prise en\ncharge', 'Solutions d\'Affaires': 6, 'PRIsME': 10 },
                              { dimension: 'Valeurs\nCulture', 'Solutions d\'Affaires': 2, 'PRIsME': 10 },
                              { dimension: 'Comités TI', 'Solutions d\'Affaires': 3, 'PRIsME': 10 },
                              { dimension: 'IA Intégrée', 'Solutions d\'Affaires': 1, 'PRIsME': 10 },
                              { dimension: '7 Piliers\n65 Tactiques', 'Solutions d\'Affaires': 4, 'PRIsME': 10 }
                            ]}>
                              <PolarGrid stroke="#ffffff40" />
                              <PolarAngleAxis 
                                dataKey="dimension" 
                                tick={{ fill: '#ffffff', fontSize: 9, fontWeight: 'bold' }}
                              />
                              <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#ffffff80', fontSize: 8 }} />
                              <Radar 
                                name="Solutions d'Affaires" 
                                dataKey="Solutions d'Affaires" 
                                stroke="#fbbf24" 
                                fill="#fbbf24" 
                                fillOpacity={0.4} 
                                strokeWidth={2} 
                              />
                              <Radar 
                                name="PRIsME" 
                                dataKey="PRIsME" 
                                stroke="#10b981" 
                                fill="#10b981" 
                                fillOpacity={0.3} 
                                strokeWidth={3} 
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                                  border: '1px solid #ffffff40',
                                  borderRadius: '8px',
                                  color: '#ffffff',
                                  fontSize: '12px'
                                }}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                          <div className="mt-2 flex justify-center gap-3 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                              <span className="text-white">Solutions d'Affaires</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 bg-green-400 rounded-full" />
                              <span className="text-white">PRIsME</span>
                            </div>
                          </div>
                        </div>

                        {/* Spider Chart 2: Aspect + Qualité (Niveaux ITIL) */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                          <div className="text-center mb-3">
                            <div className="inline-block bg-red-500/30 px-4 py-1.5 rounded-full mb-2">
                              <span className="text-lg font-black text-white">22%</span>
                            </div>
                            <h4 className="text-sm font-bold text-white">Aspect + Qualité Livrable</h4>
                            <p className="text-xs text-indigo-200 mt-1">Niveaux ITIL 1-5 (Maturité)</p>
                          </div>
                          <ResponsiveContainer width="100%" height={280}>
                            <RadarChart data={[
                              { dimension: 'Plateforme\nTechno', 'Solutions d\'Affaires': 1.5, 'PRIsME': 9 },
                              { dimension: 'Gouvernance', 'Solutions d\'Affaires': 2.5, 'PRIsME': 9 },
                              { dimension: 'Portails\nClients', 'Solutions d\'Affaires': 0.5, 'PRIsME': 10 },
                              { dimension: 'Documents\nITIL/COBIT', 'Solutions d\'Affaires': 2, 'PRIsME': 9.5 },
                              { dimension: 'Méthodologie', 'Solutions d\'Affaires': 3.5, 'PRIsME': 9 },
                              { dimension: 'Prise en\ncharge', 'Solutions d\'Affaires': 4, 'PRIsME': 9.5 },
                              { dimension: 'Valeurs\nCulture', 'Solutions d\'Affaires': 1.5, 'PRIsME': 9 },
                              { dimension: 'Comités TI', 'Solutions d\'Affaires': 2, 'PRIsME': 9.5 },
                              { dimension: 'IA Intégrée', 'Solutions d\'Affaires': 0.5, 'PRIsME': 9 },
                              { dimension: '7 Piliers\n65 Tactiques', 'Solutions d\'Affaires': 2.5, 'PRIsME': 9.5 }
                            ]}>
                              <PolarGrid stroke="#ffffff40" />
                              <PolarAngleAxis 
                                dataKey="dimension" 
                                tick={{ fill: '#ffffff', fontSize: 9, fontWeight: 'bold' }}
                              />
                              <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#ffffff80', fontSize: 8 }} />
                              <Radar 
                                name="Solutions d'Affaires" 
                                dataKey="Solutions d'Affaires" 
                                stroke="#f87171" 
                                fill="#f87171" 
                                fillOpacity={0.4} 
                                strokeWidth={2} 
                              />
                              <Radar 
                                name="PRIsME" 
                                dataKey="PRIsME" 
                                stroke="#34d399" 
                                fill="#34d399" 
                                fillOpacity={0.3} 
                                strokeWidth={3} 
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                                  border: '1px solid #ffffff40',
                                  borderRadius: '8px',
                                  color: '#ffffff',
                                  fontSize: '12px'
                                }}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                          <div className="mt-2 flex justify-center gap-3 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 bg-red-400 rounded-full" />
                              <span className="text-white">Solutions d'Affaires (Niveau 2-3 ITIL)</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 bg-emerald-400 rounded-full" />
                              <span className="text-white">PRIsME (Niveau 4-5 ITIL)</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Cartes des Avantages PRIsME */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                        {/* Carte 1: Plateforme Technologique */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all">
                          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-2 rounded-lg w-fit mb-2">
                            <Database className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1">Plateforme PRIsME</h4>
                          <p className="text-xs text-indigo-100 mb-2">
                            Plateforme web complète avec portails clients dédiés, génération automatique de documents, IA intégrée, et interface moderne.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-green-300">
                            <CheckCircle className="w-4 h-4" />
                            <span>+700% vs document statique</span>
                          </div>
                        </div>

                        {/* Carte 2: Gouvernance Structurée */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all">
                          <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-lg w-fit mb-2">
                            <Award className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1">Gouvernance Automatisée</h4>
                          <p className="text-xs text-indigo-100 mb-2">
                            Comités TI structurés avec génération automatique de résumés IA, matrices RACI complètes, et processus ITIL 4 intégrés.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-green-300">
                            <CheckCircle className="w-4 h-4" />
                            <span>100% conforme ITIL 4</span>
                          </div>
                        </div>

                        {/* Carte 3: Documents Standardisés */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all">
                          <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-2 rounded-lg w-fit mb-2">
                            <FileText className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1">Documents ITIL/COBIT</h4>
                          <p className="text-xs text-indigo-100 mb-2">
                            8 types de documents générés automatiquement : Plan Directeur, Roadmap, ODS, Budget, RACI, Registres, tous conformes ITIL 4 & COBIT.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-green-300">
                            <CheckCircle className="w-4 h-4" />
                            <span>8 documents standardisés</span>
                          </div>
                        </div>

                        {/* Carte 4: Portails Clients */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all">
                          <div className="bg-gradient-to-br from-indigo-500 to-blue-500 p-2 rounded-lg w-fit mb-2">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1">Portails PRIsME Client</h4>
                          <p className="text-xs text-indigo-100 mb-2">
                            Chaque client a son portail dédié avec accès à ses documents, projets, KPIs, et communication transparente 24/7.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-green-300">
                            <CheckCircle className="w-4 h-4" />
                            <span>Transparence totale</span>
                          </div>
                        </div>

                        {/* Carte 5: Méthodologie Holistique */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all">
                          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 p-2 rounded-lg w-fit mb-2">
                            <Target className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1">7 Piliers + 65 Tactiques</h4>
                          <p className="text-xs text-indigo-100 mb-2">
                            Cadre Référentiel BZ complet avec méthodologie ASWAS → ASIS → TARGET, roadmap structurée, et KPI mesurables.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-green-300">
                            <CheckCircle className="w-4 h-4" />
                            <span>65 tactiques structurées</span>
                          </div>
                        </div>

                        {/* Carte 6: IA Intégrée */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all">
                          <div className="bg-gradient-to-br from-pink-500 to-rose-500 p-2 rounded-lg w-fit mb-2">
                            <Sparkles className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1">IA Intégrée</h4>
                          <p className="text-xs text-indigo-100 mb-2">
                            Assistant IA avec base de connaissances synchronisée, génération automatique de documents, et analyse intelligente des besoins.
                          </p>
                          <div className="flex items-center gap-2 text-xs text-green-300">
                            <CheckCircle className="w-4 h-4" />
                            <span>IA + Expertise humaine</span>
                          </div>
                        </div>
                      </div>

                      {/* Métriques Comparatives */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                          <p className="text-xl font-black text-white mb-1">10 jours</p>
                          <p className="text-xs text-indigo-200">vs 1 an (5 personnes)</p>
                          <p className="text-xs text-green-300 mt-1">Développement PRIsME</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                          <p className="text-xl font-black text-white mb-1">8</p>
                          <p className="text-xs text-indigo-200">types de documents</p>
                          <p className="text-xs text-green-300 mt-1">Générés automatiquement</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                          <p className="text-xl font-black text-white mb-1">69</p>
                          <p className="text-xs text-indigo-200">tactiques structurées</p>
                          <p className="text-xs text-green-300 mt-1">vs approche manuelle</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                          <p className="text-xl font-black text-white mb-1">100%</p>
                          <p className="text-xs text-indigo-200">conforme ITIL/COBIT</p>
                          <p className="text-xs text-green-300 mt-1">Standardisé</p>
                        </div>
                      </div>

                      {/* Section Pédagogique - Commentaires Constructifs */}
                      <div className="bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 backdrop-blur-sm rounded-lg p-4 mb-4 border-2 border-blue-400/50">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="bg-blue-500 p-2 rounded-lg">
                            <BookOpen className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base font-black text-white mb-2">📚 Observations Constructives & Recommandations Pédagogiques</h4>
                            <p className="text-sm text-blue-100 mb-4">
                              Analyse détaillée de chaque point avec explications pédagogiques pour comprendre pourquoi c'est important et comment l'améliorer.
                            </p>

                            {/* Point 1: Terminologie */}
                            <div className="bg-white/10 rounded-lg p-3 mb-3 border border-white/20">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="bg-red-500/30 px-2 py-1 rounded text-xs font-bold text-white">1</div>
                                <div className="flex-1">
                                  <h5 className="text-sm font-bold text-white mb-1">Terminologie Non Normée</h5>
                                  <p className="text-xs text-blue-100 mb-2">
                                    <strong className="text-red-300">Problème identifié :</strong> Utilisation de termes génériques ("Audit de sécurité", "Bilan de santé technologique") au lieu de terminologie standardisée ITIL/COBIT/NIST.
                                  </p>
                                  <div className="bg-red-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">❌ Termes utilisés actuellement :</p>
                                    <ul className="text-xs text-red-200 space-y-1 ml-4 list-disc">
                                      <li>"Audit de sécurité"</li>
                                      <li>"Bilan de santé technologique"</li>
                                      <li>"Comité TI" (générique)</li>
                                    </ul>
                                  </div>
                                  <div className="bg-green-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">✅ Termes normés à utiliser :</p>
                                    <ul className="text-xs text-green-200 space-y-1 ml-4 list-disc">
                                      <li><strong>"Évaluation de sécurité selon NIST CSF 2.0"</strong> ou <strong>"Audit de conformité ISO 27001:2022"</strong></li>
                                      <li><strong>"Diagnostic TI selon COBIT 2019"</strong> ou <strong>"Évaluation de maturité ITIL 4"</strong></li>
                                      <li><strong>"Comité de gouvernance TI (ITIL 4 - Governance & Management)"</strong></li>
                                    </ul>
                                  </div>
                                  <div className="bg-blue-500/20 rounded p-2">
                                    <p className="text-xs text-white font-semibold mb-1">💡 Pourquoi c'est important :</p>
                                    <p className="text-xs text-blue-100 leading-relaxed">
                                      La terminologie normée crée un <strong className="text-white">langage commun</strong> avec les clients, démontre votre <strong className="text-white">expertise</strong> et votre compréhension des standards internationaux. 
                                      Un client qui voit "NIST CSF 2.0" ou "COBIT 2019" comprend immédiatement que vous travaillez selon des <strong className="text-white">standards reconnus mondialement</strong>, pas selon une approche maison. 
                                      C'est la différence entre un consultant qui "fait de la sécurité" et un expert certifié qui applique des frameworks internationaux.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Point 2: Hiérarchie Stratégique */}
                            <div className="bg-white/10 rounded-lg p-3 mb-3 border border-white/20">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="bg-orange-500/30 px-2 py-1 rounded text-xs font-bold text-white">2</div>
                                <div className="flex-1">
                                  <h5 className="text-sm font-bold text-white mb-1">Absence de Hiérarchie Stratégique → Tactique → Opérationnel</h5>
                                  <p className="text-xs text-blue-100 mb-2">
                                    <strong className="text-orange-300">Problème identifié :</strong> Tous les livrables sont présentés au même niveau, sans distinction entre vision stratégique, planification tactique et exécution opérationnelle.
                                  </p>
                                  <div className="bg-orange-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">❌ Approche actuelle :</p>
                                    <p className="text-xs text-orange-200">
                                      Tous les parcours (A, B, C, D, E) sont présentés de manière équivalente, sans hiérarchie temporelle ou stratégique claire.
                                    </p>
                                  </div>
                                  <div className="bg-green-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">✅ Structure recommandée :</p>
                                    <ul className="text-xs text-green-200 space-y-1 ml-4 list-disc">
                                      <li><strong>🎯 STRATÉGIQUE (3-5 ans) :</strong> Plan Directeur TI - Vision long terme, alignement business, roadmap macro</li>
                                      <li><strong>📊 TACTIQUE (12-24 mois) :</strong> Roadmap Annuel, Budget TI, ODS - Planification à moyen terme, priorités</li>
                                      <li><strong>⚙️ OPÉRATIONNEL (trimestre/mois) :</strong> Roadmap détaillée, Suivi projets - Exécution, livraison, KPIs</li>
                                    </ul>
                                  </div>
                                  <div className="bg-blue-500/20 rounded p-2">
                                    <p className="text-xs text-white font-semibold mb-1">💡 Pourquoi c'est important :</p>
                                    <p className="text-xs text-blue-100 leading-relaxed">
                                      Un client a besoin de comprendre <strong className="text-white">où il va (stratégique)</strong>, <strong className="text-white">comment il y arrive (tactique)</strong> et <strong className="text-white">ce qu'il fait aujourd'hui (opérationnel)</strong>. 
                                      Sans cette hiérarchie, le client navigue à vue. La méthodologie PRIsME structure cette progression : 
                                      <strong className="text-white">ASWAS (où on était) → ASIS (où on est) → TARGET (où on veut être)</strong>. 
                                      C'est la différence entre un "plan d'action" générique et une <strong className="text-white">vision stratégique structurée</strong>.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Point 3: Posture TI Évolutive */}
                            <div className="bg-white/10 rounded-lg p-3 mb-3 border border-white/20">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="bg-yellow-500/30 px-2 py-1 rounded text-xs font-bold text-white">3</div>
                                <div className="flex-1">
                                  <h5 className="text-sm font-bold text-white mb-1">Manque de Profondeur sur la Posture TI Évolutive</h5>
                                  <p className="text-xs text-blue-100 mb-2">
                                    <strong className="text-yellow-300">Problème identifié :</strong> Pas de mention des niveaux de maturité ITIL, pas de vision d'évolution progressive, approche réactive plutôt que proactive.
                                  </p>
                                  <div className="bg-yellow-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">❌ Approche actuelle :</p>
                                    <p className="text-xs text-yellow-200">
                                      Présentation de services sans mention de maturité, sans vision d'évolution, sans méthodologie ASWAS → ASIS → TARGET.
                                    </p>
                                  </div>
                                  <div className="bg-green-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">✅ Approche recommandée :</p>
                                    <ul className="text-xs text-green-200 space-y-1 ml-4 list-disc">
                                      <li><strong>Niveau 1 (Ad hoc) :</strong> Pas de processus, réactif, chaos</li>
                                      <li><strong>Niveau 2 (Repeatable) :</strong> Processus informels, dépendant des personnes</li>
                                      <li><strong>Niveau 3 (Defined) :</strong> Processus documentés, standardisés</li>
                                      <li><strong>Niveau 4 (Managed) :</strong> Processus mesurés, contrôlés, KPIs</li>
                                      <li><strong>Niveau 5 (Optimized) :</strong> Amélioration continue, innovation, excellence</li>
                                    </ul>
                                  </div>
                                  <div className="bg-blue-500/20 rounded p-2">
                                    <p className="text-xs text-white font-semibold mb-1">💡 Pourquoi c'est important :</p>
                                    <p className="text-xs text-blue-100 leading-relaxed">
                                      Un client doit comprendre <strong className="text-white">où il est aujourd'hui</strong> (ASIS) et <strong className="text-white">où il veut être</strong> (TARGET). 
                                      Les niveaux ITIL 1-5 donnent un <strong className="text-white">langage commun</strong> et une <strong className="text-white">vision d'évolution claire</strong>. 
                                      Sans cela, le client ne comprend pas la <strong className="text-white">valeur de la progression</strong>. 
                                      C'est la différence entre "on fait de la sécurité" et "on évolue de niveau 2 à niveau 4 en cybersécurité selon NIST CSF 2.0".
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Point 4: Approche Manuelle */}
                            <div className="bg-white/10 rounded-lg p-3 mb-3 border border-white/20">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="bg-purple-500/30 px-2 py-1 rounded text-xs font-bold text-white">4</div>
                                <div className="flex-1">
                                  <h5 className="text-sm font-bold text-white mb-1">Approche Manuelle vs Industrialisée</h5>
                                  <p className="text-xs text-blue-100 mb-2">
                                    <strong className="text-purple-300">Problème identifié :</strong> Pas de mention de plateforme, d'IA, d'automatisation, de portails clients. Approche "artisanale" plutôt qu'industrialisée.
                                  </p>
                                  <div className="bg-purple-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">❌ Approche actuelle :</p>
                                    <p className="text-xs text-purple-200">
                                      Documents créés manuellement, processus non automatisés, pas de plateforme, pas d'IA intégrée.
                                    </p>
                                  </div>
                                  <div className="bg-green-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">✅ Approche PRIsME :</p>
                                    <ul className="text-xs text-green-200 space-y-1 ml-4 list-disc">
                                      <li><strong>Plateforme PRIsME :</strong> Génération automatique de documents ITIL/COBIT</li>
                                      <li><strong>IA Intégrée :</strong> Analyse intelligente, suggestions, optimisation</li>
                                      <li><strong>Portails Clients :</strong> Transparence 24/7, accès documents, KPIs</li>
                                      <li><strong>Standardisation :</strong> 8 types de documents conformes ITIL/COBIT</li>
                                      <li><strong>Reproductibilité :</strong> Même qualité pour tous les clients</li>
                                    </ul>
                                  </div>
                                  <div className="bg-blue-500/20 rounded p-2">
                                    <p className="text-xs text-white font-semibold mb-1">💡 Pourquoi c'est important :</p>
                                    <p className="text-xs text-blue-100 leading-relaxed">
                                      Une approche manuelle = <strong className="text-white">qualité variable</strong>, <strong className="text-white">temps perdu</strong>, <strong className="text-white">erreurs humaines</strong>. 
                                      Une approche industrialisée = <strong className="text-white">qualité constante</strong>, <strong className="text-white">efficacité maximale</strong>, <strong className="text-white">scalabilité</strong>. 
                                      PRIsME transforme un processus artisanal en <strong className="text-white">machine à excellence reproductible</strong>. 
                                      C'est la différence entre un artisan qui fait un document parfait une fois et une usine qui produit 100 documents parfaits à chaque fois.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Point 5: Structure Méthodologique */}
                            <div className="bg-white/10 rounded-lg p-3 mb-3 border border-white/20">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="bg-indigo-500/30 px-2 py-1 rounded text-xs font-bold text-white">5</div>
                                <div className="flex-1">
                                  <h5 className="text-sm font-bold text-white mb-1">Manque de Structure Méthodologique</h5>
                                  <p className="text-xs text-blue-100 mb-2">
                                    <strong className="text-indigo-300">Problème identifié :</strong> Pas de référence au Cadre Référentiel BZ, pas de lien avec les 7 Piliers et 69 Tactiques, approche "à la carte" plutôt que structurée.
                                  </p>
                                  <div className="bg-indigo-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">❌ Approche actuelle :</p>
                                    <p className="text-xs text-indigo-200">
                                      Services présentés de manière isolée, sans cadre de référence, sans méthodologie structurée.
                                    </p>
                                  </div>
                                  <div className="bg-green-500/20 rounded p-2 mb-2">
                                    <p className="text-xs text-white font-semibold mb-1">✅ Approche PRIsME :</p>
                                    <ul className="text-xs text-green-200 space-y-1 ml-4 list-disc">
                                      <li><strong>Cadre Référentiel BZ :</strong> 7 Piliers stratégiques, 65 Tactiques structurées</li>
                                      <li><strong>Méthodologie ASWAS → ASIS → TARGET :</strong> Vision d'évolution claire</li>
                                      <li><strong>Standards ITIL/COBIT/NIST :</strong> Conformité internationale</li>
                                      <li><strong>Roadmap Structurée :</strong> Progression logique et mesurable</li>
                                      <li><strong>KPIs & Mesures :</strong> Suivi de la maturité et de la valeur</li>
                                    </ul>
                                  </div>
                                  <div className="bg-blue-500/20 rounded p-2">
                                    <p className="text-xs text-white font-semibold mb-1">💡 Pourquoi c'est important :</p>
                                    <p className="text-xs text-blue-100 leading-relaxed">
                                      Sans structure méthodologique, chaque projet est un <strong className="text-white">one-off</strong>, sans cohérence, sans vision d'ensemble. 
                                      Le Cadre Référentiel BZ avec ses 7 Piliers et 65 Tactiques crée une <strong className="text-white">langue commune</strong>, une <strong className="text-white">vision holistique</strong> et une <strong className="text-white">reproductibilité</strong>. 
                                      C'est la différence entre "on fait de la sécurité" et "on applique le Pilier 5 (Gouvernance Cybersécurité) avec les 6 tactiques NIST CSF 2.0 (Gouvernance, Identifier, Protéger, Détecter, Répondre, Récupérer)". 
                                      Le client comprend <strong className="text-white">où chaque service s'insère</strong> dans la vision globale.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Conclusion Pédagogique */}
                            <div className="bg-gradient-to-r from-green-500/30 to-emerald-500/30 rounded-lg p-3 border-2 border-green-400/50">
                              <p className="text-xs text-white font-bold mb-2">🎓 Conclusion Pédagogique :</p>
                              <p className="text-xs text-green-100 leading-relaxed">
                                Ces observations ne sont pas des critiques, mais des <strong className="text-white">opportunités d'évolution</strong>. 
                                Votre document "Solutions d'Affaires" a une <strong className="text-white">bonne base</strong> et une <strong className="text-white">compréhension solide des besoins clients</strong>. 
                                En intégrant la <strong className="text-white">terminologie normée</strong>, la <strong className="text-white">hiérarchie stratégique</strong>, la <strong className="text-white">vision évolutive</strong>, l'<strong className="text-white">approche industrialisée</strong> et la <strong className="text-white">structure méthodologique</strong> de PRIsME, 
                                vous transformez une <strong className="text-white">bonne approche commerciale</strong> en une <strong className="text-white">solution de niveau 4-5 ITIL</strong> qui transforme réellement l'industrie TI.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Message Final - Direct et Professionnel */}
                      <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-red-500/20 backdrop-blur-sm rounded-lg p-4 border-2 border-amber-400/50">
                        <div className="flex items-start gap-3">
                          <div className="bg-amber-500 p-2 rounded-lg">
                            <Target className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base font-black text-white mb-2">La Réalité : Deux Niveaux, Deux Ligue</h4>
                            <p className="text-sm text-white leading-relaxed mb-3">
                              Votre document "Solutions d'Affaires" démontre une <strong className="text-amber-200">bonne compréhension des besoins clients</strong> et une approche solide. 
                              Cependant, comme le montrent les graphiques ci-dessus, <strong className="text-white">nous ne sommes pas dans la même ligue en termes de maturité, de qualité de livrable et de profondeur d'exécution</strong>.
                            </p>
                            
                            <div className="bg-white/10 rounded-lg p-3 mb-3 border border-white/20">
                              <p className="text-xs text-white font-semibold mb-2">
                                📊 <strong>Les chiffres parlent :</strong>
                              </p>
                              <ul className="space-y-1.5 text-xs text-white/90 ml-4 list-disc">
                                <li><strong className="text-amber-200">35% de couverture</strong> vs <strong className="text-green-300">100% avec PRIsME</strong> (aspect fonctionnel)</li>
                                <li><strong className="text-red-300">22% de maturité globale</strong> vs <strong className="text-emerald-300">90%+ avec PRIsME</strong> (qualité ITIL niveau 4-5)</li>
                                <li><strong className="text-amber-200">Niveau 2-3 ITIL</strong> (Repeatable/Defined) vs <strong className="text-green-300">Niveau 4-5 ITIL</strong> (Managed/Optimized)</li>
                                <li><strong className="text-amber-200">Document statique</strong> vs <strong className="text-green-300">Plateforme complète avec IA, portails clients, gouvernance automatisée</strong></li>
                              </ul>
                            </div>

                            <div className="space-y-2 text-xs text-white/90 mb-3">
                              <p className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">Votre approche</strong> = Bonne base, mais <strong className="text-amber-200">manque de profondeur, de standardisation et de reproductibilité</strong></span>
                              </p>
                              <p className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">PRIsME</strong> = <strong className="text-green-300">Solution complète, structurée, conforme ITIL/COBIT, avec gouvernance, portails clients, IA intégrée et 65 tactiques structurées</strong></span>
                              </p>
                              <p className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">Le choix est simple :</strong> Continuer en silo avec une approche manuelle (niveau 2-3) <strong className="text-white">OU</strong> intégrer PRIsME et passer au niveau 4-5 (Managed/Optimized)</span>
                              </p>
                            </div>

                            <div className="mt-3 p-3 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-lg border border-red-400/30">
                              <p className="text-xs text-white font-bold leading-relaxed">
                                ⚠️ <strong>Message clair :</strong> Nous ne sommes pas en compétition. Nous sommes dans des ligues différentes. 
                                Votre document est une <strong className="text-amber-200">bonne base</strong>, mais PRIsME représente <strong className="text-green-300">25 ans d'expertise structurée, automatisée et industrialisée</strong>. 
                                La question n'est pas "qui a raison", mais plutôt : <strong className="text-white">voulez-vous continuer à travailler en silo avec une approche manuelle, ou voulez-vous intégrer une solution qui transforme réellement l'industrie TI ?</strong>
                              </p>
                              <p className="text-xs text-white/80 mt-2 italic">
                                💡 <strong>Le no-brainer :</strong> Laissez-nous vous montrer comment PRIsME peut structurer, automatiser et transformer votre approche en solution de niveau 4-5 ITIL. 
                                Sinon, continuez en silo - mais sachez que nous ne sommes pas dans la même ligue.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section : Métaphore Tronc → Branches → Feuilles */}
                      <div className="bg-gradient-to-br from-green-900/50 via-emerald-900/50 to-teal-900/50 backdrop-blur-sm rounded-lg p-4 mb-4 border-2 border-green-400/50">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="bg-green-500 p-2 rounded-lg">
                            <Target className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base font-black text-white mb-2">🌳 La Métaphore de l'Arbre : Pourquoi le Haut de Gamme Passe par Tronc → Branches → Feuilles</h4>
                            <p className="text-xs text-green-100 mb-4">
                              Le cerveau est faible et limité. On doit lui placer l'information dans des petites cases. 
                              <strong className="text-white"> Le haut de gamme structure comme un arbre : on commence par le tronc, puis les branches, puis les feuilles.</strong>
                            </p>

                            {/* Comparaison Visuelle */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                              {/* Approche PRIsME (Correcte) */}
                              <div className="bg-green-500/20 rounded-lg p-3 border-2 border-green-400/50">
                                <div className="flex items-center gap-2 mb-3">
                                  <CheckCircle className="w-5 h-5 text-green-400" />
                                  <h5 className="text-sm font-bold text-white">✅ Approche PRIsME (Haut de Gamme)</h5>
                                </div>
                                <div className="space-y-3">
                                  <div className="bg-amber-500/30 rounded-lg p-2 border-l-4 border-amber-400">
                                    <div className="text-amber-200 font-bold text-xs mb-1">🌲 1. LE TRONC (Stratégique)</div>
                                    <p className="text-xs text-white/90">
                                      <strong className="text-white">La fondation. Les principes. Les valeurs. Ce qui ne change jamais.</strong><br/>
                                      <span className="text-amber-200">• Plan Directeur TI (3-5 ans)</span><br/>
                                      <span className="text-amber-200">• Vision stratégique alignée business</span><br/>
                                      <span className="text-amber-200">• Cadre Référentiel BZ (7 Piliers)</span><br/>
                                      <span className="text-amber-200">• Méthodologie ASWAS → ASIS → TARGET</span>
                                    </p>
                                    <p className="text-xs text-amber-200 font-semibold mt-1">→ Commencez ici. Toujours.</p>
                                  </div>
                                  <div className="bg-green-500/30 rounded-lg p-2 border-l-4 border-green-400">
                                    <div className="text-green-200 font-bold text-xs mb-1">🌿 2. LES BRANCHES (Tactique)</div>
                                    <p className="text-xs text-white/90">
                                      <strong className="text-white">Les catégories. Les domaines. Les grandes divisions.</strong><br/>
                                      <span className="text-green-200">• Roadmap Annuel (12-24 mois)</span><br/>
                                      <span className="text-green-200">• Budget TI structuré</span><br/>
                                      <span className="text-green-200">• ODS (Offre de Service)</span><br/>
                                      <span className="text-green-200">• 65 Tactiques structurées par pilier</span>
                                    </p>
                                    <p className="text-xs text-green-200 font-semibold mt-1">→ Ensuite, explorez les branches.</p>
                                  </div>
                                  <div className="bg-emerald-500/30 rounded-lg p-2 border-l-4 border-emerald-400">
                                    <div className="text-emerald-200 font-bold text-xs mb-1">🍃 3. LES FEUILLES (Opérationnel)</div>
                                    <p className="text-xs text-white/90">
                                      <strong className="text-white">Les détails. Les processus spécifiques. Les millions de petites choses.</strong><br/>
                                      <span className="text-emerald-200">• Roadmap détaillée (trimestre/mois)</span><br/>
                                      <span className="text-emerald-200">• Suivi projets opérationnels</span><br/>
                                      <span className="text-emerald-200">• KPIs quotidiens</span><br/>
                                      <span className="text-emerald-200">• Exécution tactique</span>
                                    </p>
                                    <p className="text-xs text-emerald-200 font-semibold mt-1">→ Finalement, les feuilles deviennent évidentes.</p>
                                  </div>
                                </div>
                                <div className="mt-3 bg-green-500/20 rounded p-2 border border-green-400/30">
                                  <p className="text-xs text-green-100 text-center font-semibold">
                                    ✅ <strong className="text-white">Résultat :</strong> Vision claire, structure solide, exécution efficace. 
                                    <strong className="text-white"> Niveau 4-5 ITIL (Managed/Optimized)</strong>
                                  </p>
                                </div>
                              </div>

                              {/* Approche Solutions d'Affaires (Inverse) */}
                              <div className="bg-red-500/20 rounded-lg p-3 border-2 border-red-400/50">
                                <div className="flex items-center gap-2 mb-3">
                                  <X className="w-5 h-5 text-red-400" />
                                  <h5 className="text-sm font-bold text-white">❌ Approche Solutions d'Affaires (Inverse)</h5>
                                </div>
                                <div className="space-y-3">
                                  <div className="bg-red-500/30 rounded-lg p-2 border-l-4 border-red-400 opacity-60">
                                    <div className="text-red-200 font-bold text-xs mb-1">🍃 1. LES FEUILLES (Opérationnel) - EN PREMIER</div>
                                    <p className="text-xs text-white/90">
                                      <strong className="text-white">On commence par les détails, sans vision d'ensemble.</strong><br/>
                                      <span className="text-red-200">• Parcours A, B, C, D, E (sans hiérarchie)</span><br/>
                                      <span className="text-red-200">• Services présentés isolément</span><br/>
                                      <span className="text-red-200">• Pas de vision stratégique claire</span><br/>
                                      <span className="text-red-200">• Approche réactive</span>
                                    </p>
                                    <p className="text-xs text-red-200 font-semibold mt-1">→ On commence par les détails. Erreur.</p>
                                  </div>
                                  <div className="bg-orange-500/30 rounded-lg p-2 border-l-4 border-orange-400 opacity-60">
                                    <div className="text-orange-200 font-bold text-xs mb-1">🌿 2. LES BRANCHES (Tactique) - ENSUITE</div>
                                    <p className="text-xs text-white/90">
                                      <strong className="text-white">On essaie de structurer après coup, sans fondation solide.</strong><br/>
                                      <span className="text-orange-200">• Pas de roadmap structurée</span><br/>
                                      <span className="text-orange-200">• Pas de budget TI aligné stratégie</span><br/>
                                      <span className="text-orange-200">• Pas de cadre de référence</span><br/>
                                      <span className="text-orange-200">• Approche "à la carte"</span>
                                    </p>
                                    <p className="text-xs text-orange-200 font-semibold mt-1">→ On essaie de créer des branches sans tronc. Fragile.</p>
                                  </div>
                                  <div className="bg-yellow-500/30 rounded-lg p-2 border-l-4 border-yellow-400 opacity-60">
                                    <div className="text-yellow-200 font-bold text-xs mb-1">🌲 3. LE TRONC (Stratégique) - EN DERNIER (ou jamais)</div>
                                    <p className="text-xs text-white/90">
                                      <strong className="text-white">Le tronc arrive trop tard, ou n'arrive jamais.</strong><br/>
                                      <span className="text-yellow-200">• Pas de Plan Directeur TI</span><br/>
                                      <span className="text-yellow-200">• Pas de vision 3-5 ans</span><br/>
                                      <span className="text-yellow-200">• Pas de méthodologie ASWAS → ASIS → TARGET</span><br/>
                                      <span className="text-yellow-200">• Pas de cadre référentiel structuré</span>
                                    </p>
                                    <p className="text-xs text-yellow-200 font-semibold mt-1">→ Le tronc manque. L'arbre est fragile.</p>
                                  </div>
                                </div>
                                <div className="mt-3 bg-red-500/20 rounded p-2 border border-red-400/30">
                                  <p className="text-xs text-red-100 text-center font-semibold">
                                    ❌ <strong className="text-white">Résultat :</strong> Vision floue, structure fragile, exécution chaotique. 
                                    <strong className="text-white"> Niveau 2-3 ITIL (Repeatable/Defined)</strong>
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Message Pédagogique */}
                            <div className="bg-gradient-to-r from-green-500/30 to-emerald-500/30 rounded-lg p-3 border border-green-400/50">
                              <p className="text-xs text-white font-bold mb-2">💡 Pourquoi c'est critique :</p>
                              <p className="text-xs text-green-100 leading-relaxed mb-2">
                                <strong className="text-white">Beaucoup plus simple de comprendre le tronc et les branches que les millions de feuilles.</strong> 
                                Si vous commencez par les feuilles (détails opérationnels), vous vous perdez dans la complexité. 
                                Si vous commencez par le tronc (vision stratégique), <strong className="text-white">les branches et les feuilles deviennent évidentes et logiques</strong>.
                              </p>
                              <p className="text-xs text-green-100 leading-relaxed">
                                <strong className="text-white">PRIsME structure. PRIsME simplifie. PRIsME clarifie.</strong> 
                                C'est la différence entre naviguer à vue (feuilles → branches → tronc) et avoir une boussole stratégique (tronc → branches → feuilles).
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PROFIL CLIENT */}
                {activeTab === 'client' && (
                  <div className="space-y-2">
                    <div className="bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-3 rounded-xl">
                          <Building2 className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-black text-gray-900">{clientMockup.nom}</h2>
                          <p className="text-sm text-gray-600 mt-1">{clientMockup.secteur}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-6 mb-8">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl">
                          <Users className="w-8 h-8 text-blue-600 mb-3" />
                          <p className="text-sm text-gray-600 mb-1">Taille entreprise</p>
                          <p className="text-3xl font-black text-gray-900">{clientMockup.tailleEntreprise}</p>
                          <p className="text-sm text-gray-600">employés</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl">
                          <DollarSign className="w-8 h-8 text-green-600 mb-3" />
                          <p className="text-sm text-gray-600 mb-1">Chiffre d'affaires</p>
                          <p className="text-3xl font-black text-gray-900">{(clientMockup.chiffreAffaires / 1000000).toFixed(1)}M$</p>
                          <p className="text-sm text-gray-600">annuel</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl">
                          <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
                          <p className="text-sm text-gray-600 mb-1">Budget TI</p>
                          <p className="text-3xl font-black text-gray-900">{(clientMockup.budgetTI / 1000).toFixed(0)}k$</p>
                          <p className="text-sm text-gray-600">{((clientMockup.budgetTI / clientMockup.chiffreAffaires) * 100).toFixed(1)}% du CA</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gray-50 p-6 rounded-xl">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-blue-600" />
                            Contact Principal
                          </h3>
                          <div className="space-y-2">
                            <p className="text-gray-700"><strong>Nom:</strong> {clientMockup.contactPrincipal.nom}</p>
                            <p className="text-gray-700"><strong>Titre:</strong> {clientMockup.contactPrincipal.titre}</p>
                            <p className="text-gray-700"><strong>Email:</strong> {clientMockup.contactPrincipal.email}</p>
                            <p className="text-gray-700"><strong>Tél:</strong> {clientMockup.contactPrincipal.telephone}</p>
                          </div>
                        </div>
                        <div className="bg-blue-50 p-6 rounded-xl">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-600" />
                            Directeur TI
                          </h3>
                          <div className="space-y-2">
                            <p className="text-gray-700"><strong>Nom:</strong> {clientMockup.directeurTI.nom}</p>
                            <p className="text-gray-700"><strong>Titre:</strong> {clientMockup.directeurTI.titre}</p>
                            <p className="text-gray-700"><strong>Email:</strong> {clientMockup.directeurTI.email}</p>
                            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                              <p className="text-xs text-blue-900 font-semibold">🤝 Partenariat BZ Inc. depuis {clientMockup.dateEvaluation}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Objectifs stratégiques</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Maturité actuelle</p>
                            <p className="text-2xl font-black text-blue-600">{clientMockup.maturiteGlobale}/5</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Objectif maturité</p>
                            <p className="text-2xl font-black text-green-600">{clientMockup.objectifMaturite}/5</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Délai objectif</p>
                            <p className="text-2xl font-black text-purple-600">{clientMockup.delaiObjectif}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer BZ Branding */}
          <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-lg">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Cadre Référentiel BZ Inc.</p>
                  <p className="text-xs text-gray-600">Méthodologie propriétaire • Standards ITIL 4 • Normes NIST 2.0</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Généré par PRIsME</p>
                <p className="text-xs text-gray-400">© 2025 BZ Inc. Inc.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CadreReferentielModal;
